import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Chip,
  IconButton,
  LinearProgress,
  Switch,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Add,
  Close,
  CloudUpload,
  Star,
  StarBorder,
  CheckCircle,
  ErrorOutline,
  Delete,
} from '@mui/icons-material';
import defaultConfig from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import RestaurantSetupWizard from './RestaurantSetupWizard';

// Helper — read the restaurant-only flag from sessionStorage. Accepts a few
// alternative shapes so we don't have to coordinate naming changes across
// every caller that writes companyDetails. Returns false if anything goes
// wrong reading the storage (e.g. SSR, parse error, missing key).
const isRestaurantCompany = () => {
  try {
    const raw = sessionStorage.getItem('companyDetails');
    if (!raw) return false;
    const d = JSON.parse(raw);
    return Boolean(
      d?.isRestaurant ?? d?.IsRestaurant ?? d?.is_restaurant ?? false,
    );
  } catch (_) {
    return false;
  }
};

/* ── Constants ─────────────────────────────── */
const STEPS = [
  { icon: '🏨', label: 'Hotel Profile', desc: 'Name, logo & rating' },
  { icon: '📍', label: 'Location & Contact', desc: 'Address & contact info' },
  { icon: '🏗️', label: 'Property Setup', desc: 'Floors, timings & facilities' },
  { icon: '🛏️', label: 'Room Types', desc: 'Add your room categories' },
  { icon: '🚪', label: 'Add Room', desc: 'Configure room details' },
  { icon: '🍽️', label: 'Restaurant & Dining', desc: 'Tables & dining setup' },
  { icon: '💰', label: 'Package Setup', desc: 'Pricing & packages' },
  { icon: '🚀', label: 'Go Live', desc: 'Review & launch' },
];

const CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
];

const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'District of Columbia',
];

const INDIA_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Puducherry',
];

const GSTIN_STATE = {
  '01': 'Jammu and Kashmir',
  '02': 'Himachal Pradesh',
  '03': 'Punjab',
  '04': 'Chandigarh',
  '05': 'Uttarakhand',
  '06': 'Haryana',
  '07': 'Delhi',
  '08': 'Rajasthan',
  '09': 'Uttar Pradesh',
  10: 'Bihar',
  11: 'Sikkim',
  12: 'Arunachal Pradesh',
  13: 'Nagaland',
  14: 'Manipur',
  15: 'Mizoram',
  16: 'Tripura',
  17: 'Meghalaya',
  18: 'Assam',
  19: 'West Bengal',
  20: 'Jharkhand',
  21: 'Odisha',
  22: 'Chhattisgarh',
  23: 'Madhya Pradesh',
  24: 'Gujarat',
  26: 'Dadra and Nagar Haveli',
  27: 'Maharashtra',
  29: 'Karnataka',
  30: 'Goa',
  31: 'Lakshadweep',
  32: 'Kerala',
  33: 'Tamil Nadu',
  34: 'Puducherry',
  36: 'Telangana',
  37: 'Andhra Pradesh',
  38: 'Ladakh',
};

const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const QUICK_ROOMS = [
  'Standard',
  'Deluxe',
  'Suite',
  'Executive Suite',
  'Premium',
  'Cottage',
  'Villa',
  'Family Room',
];

// Shared dropdown MenuProps so long items (state names, room types, etc.)
// wrap nicely and the panel is wide enough  no more truncated text.
const DROPDOWN_SELECT_PROPS = {
  MenuProps: {
    PaperProps: {
      sx: {
        maxHeight: 320,
        minWidth: 240,
        boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
        border: '1px solid #E2E8F0',
        '& .MuiMenuItem-root': {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: 1.35,
          fontSize: 13,
          py: 1,
          minHeight: 'unset',
        },
      },
    },
  },
};

const QUICK_AREAS = [
  'Indoor',
  'Outdoor',
  'Rooftop',
  'Pool Side',
  'Garden',
  'Bar Lounge',
  'VIP Section',
  'Banquet Hall',
];

/* ── Shared helpers ────────────────────────── */
const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: 11,
      fontWeight: 700,
      color: '#475569',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      mb: 0.7,
      display: 'flex',
      alignItems: 'center',
      gap: 0.3,
    }}>
    {children}
    {required && (
      <Box
        component='span'
        sx={{ color: '#f43f5e', fontSize: 13, lineHeight: 1 }}>
        *
      </Box>
    )}
  </Typography>
);

const SectionHead = ({ children, sx: sxExtra = {} }) => (
  <Typography
    sx={{
      fontSize: 12,
      fontWeight: 700,
      color: '#1A56DB',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      mb: 1.5,
      mt: 0.5,
      borderBottom: '1px solid rgba(26,86,219,0.12)',
      pb: 0.7,
      ...sxExtra,
    }}>
    {children}
  </Typography>
);

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? 56 : -56, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? -56 : 56, opacity: 0 }),
};

/* ══════════════════════════════════════════════
   STEP 0 — Hotel Profile
══════════════════════════════════════════════ */
function Step0({ form, upd, fileRef, imgRef }) {
  const addImages = (files) => {
    const newImgs = Array.from(files).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    upd('images', [...form.images, ...newImgs]);
  };

  

  return (
    <>
      {/* MOBILE: smaller heading */}
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Tell us about your hotel
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
        This information appears on booking vouchers and guest communications.
      </Typography>

      <SectionHead>Hotel Logo</SectionHead>
      <Box
        onClick={() => fileRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: form.logoFile ? '#1A56DB' : '#CBD5E1',
          borderRadius: 3,
          p: 2.5,
          textAlign: 'center',
          cursor: 'pointer',
          mb: 3,
          background: form.logoFile ? 'rgba(26,86,219,0.04)' : '#fafbff',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: '#1A56DB',
            background: 'rgba(26,86,219,0.04)',
          },
        }}>
        {form.logoPreview ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}>
            <Box
              component='img'
              src={form.logoPreview}
              alt='Logo'
              sx={{
                width: 64,
                height: 64,
                objectFit: 'contain',
                borderRadius: 2,
                border: '1px solid #E2E8F0',
              }}
            />
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                sx={{ fontSize: 13, fontWeight: 600, color: '#1A56DB' }}>
                Logo uploaded ✓
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                Click to change
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <CloudUpload sx={{ color: '#CBD5E1', fontSize: 38, mb: 1 }} />
            <Typography
              sx={{ fontSize: 13, color: '#64748B', fontWeight: 500 }}>
              Click to upload hotel logo{' '}
              <Box component='span' sx={{ color: '#f43f5e' }}>
                *
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5 }}>
              PNG, JPG — Square recommended
            </Typography>
          </>
        )}
      </Box>
      <input
        ref={fileRef}
        type='file'
        accept='image/*'
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          upd('logoFile', file);
          upd('logoPreview', URL.createObjectURL(file));
        }}
      />

      <SectionHead>Hotel Images</SectionHead>
      <Button
        variant='outlined'
        startIcon={<CloudUpload />}
        onClick={() => imgRef.current?.click()}
        sx={{
          mb: 2,
          borderColor: '#1A56DB',
          color: '#1A56DB',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { background: 'rgba(26,86,219,0.05)' },
        }}>
        Upload Hotel Images{' '}
        <Box component='span' sx={{ color: '#f43f5e', ml: 0.3 }}>
          *
        </Box>
      </Button>
      <input
        ref={imgRef}
        type='file'
        accept='image/*'
        multiple
        hidden
        onChange={(e) => addImages(e.target.files)}
      />

      {form.images.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
          {form.images.map((img, i) => (
            <Box
              key={i}
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #E2E8F0',
              }}>
              <Box
                component='img'
                src={img.preview}
                alt=''
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <IconButton
                size='small'
                onClick={() =>
                  upd(
                    'images',
                    form.images.filter((_, idx) => idx !== i),
                  )
                }
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: 20,
                  height: 20,
                  '&:hover': { bgcolor: '#fff', color: '#f43f5e' },
                }}>
                <Delete sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <SectionHead>Basic Details</SectionHead>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <FieldLabel required>Hotel Name</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='e.g. Nature Farm Resort'
            value={form.hotelName}
            onChange={(e) => upd('hotelName', e.target.value)}
            error={!form.hotelName.trim()}
            helperText={!form.hotelName.trim() ? 'Required' : ''}
          />
        </Grid>
        <Grid item xs={12}>
          <FieldLabel required>Description</FieldLabel>
          <TextField
            fullWidth
            size='small'
            multiline
            rows={2}
            placeholder='Describe your hotel — facilities, ambience, unique features'
            value={form.description}
            onChange={(e) => upd('description', e.target.value)}
            error={!form.description.trim()}
            helperText={!form.description.trim() ? 'Required' : ''}
          />
        </Grid>
        {/* MOBILE: each takes full width on xs */}
        <Grid item xs={12} sm={6}>
          <FieldLabel required>Currency</FieldLabel>
          <Autocomplete
            fullWidth
            options={CURRENCIES}
            getOptionLabel={(o) => `${o.name} (${o.code}) — ${o.symbol}`}
            value={
              CURRENCIES.find((c) => c.code === form.currency) || CURRENCIES[0]
            }
            onChange={(_, v) => upd('currency', v?.code || 'INR')}
              sx={{
      '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
        minWidth: '180px !important',
      },
    }}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                placeholder='Select currency'
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>24-Hour Hotel</FieldLabel>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderRadius: 2,
              border: '1.5px solid',
              transition: 'all 0.2s',
              borderColor: form.is24Hour ? '#1A56DB' : '#E2E8F0',
              background: form.is24Hour ? 'rgba(26,86,219,0.04)' : '#fff',
            }}>
            <Typography
              sx={{ fontSize: 13, fontWeight: 500, color: '#475569' }}>
              {form.is24Hour
                ? '✅ Open 24 hours'
                : 'Set check-in / check-out times'}
            </Typography>
            <Switch
              checked={form.is24Hour}
              onChange={(e) => upd('is24Hour', e.target.checked)}
              sx={{
                width: 44,
                height: 24,
                padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: 0,
                  margin: '2px',
                  transitionDuration: '300ms',
                  '&.Mui-checked': {
                    transform: 'translateX(20px)',
                    color: '#fff',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#1A56DB',
                      opacity: 1,
                    },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 20, height: 20,
                  boxShadow: '0 2px 4px rgba(15,23,42,0.2)',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 12,
                  backgroundColor: '#CBD5E1',
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <SectionHead>Star Rating</SectionHead>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
        }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <IconButton
            key={n}
            onClick={() => upd('starRating', n)}
            sx={{
              p: 0.5,
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.2)' },
            }}>
            {n <= form.starRating ? (
              <Star sx={{ color: '#f59e0b', fontSize: { xs: 28, md: 34 } }} />
            ) : (
              <StarBorder
                sx={{ color: '#CBD5E1', fontSize: { xs: 28, md: 34 } }}
              />
            )}
          </IconButton>
        ))}
        <Typography
          sx={{ ml: 1, color: '#64748B', fontSize: 13, fontWeight: 500 }}>
          {form.starRating}-Star Property
        </Typography>
      </Box>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 1 — Location & Contact
══════════════════════════════════════════════ */
function Step1({ form, upd, cities, isIndia, fetchGSTDetails,fetchCityStateByPincode  }) {
  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Where is your hotel located?
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
        This appears on invoices, vouchers and all guest communications.
      </Typography>

      {/* India: country badge */}
      {isIndia && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2.5,
            border: '1.5px solid rgba(26,86,219,0.25)',
            background: 'rgba(26,86,219,0.05)',
            mb: 3,
          }}>
          <Typography sx={{ fontSize: 20 }}>🇮🇳</Typography>
          <Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: 13, color: '#1A56DB' }}>
              India — GST Registered Property
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
              GST & Food License fields available below
            </Typography>
          </Box>
        </Box>
      )}

      {/* India: GSTIN auto-fill */}
      {isIndia && (
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: form.gstVerified ? 'rgba(16,185,129,0.4)' : '#E2E8F0',
            background: form.gstVerified
              ? 'rgba(16,185,129,0.04)'
              : 'rgba(26,86,219,0.02)',
          }}>
          <FieldLabel>GSTIN — Auto-fill State & Details</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='e.g. 27AABCU9603R1ZX  (15 characters)'
            value={form.gstNumber}
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/\s/g, '');
              upd('gstNumber', val);
              upd('gstVerified', false);
              upd('gstError', '');
              if (val.length === 15) fetchGSTDetails(val);
            }}
            inputProps={{
              maxLength: 15,
              style: { letterSpacing: '0.1em', fontWeight: 600 },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {form.gstFetching && (
                    <CircularProgress size={16} sx={{ color: '#1A56DB' }} />
                  )}
                  {!form.gstFetching && form.gstVerified && (
                    <Tooltip title='GST verified — fields auto-filled'>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </Tooltip>
                  )}
                  {!form.gstFetching && form.gstError && (
                    <Tooltip title={form.gstError}>
                      <ErrorOutline sx={{ color: '#f59e0b', fontSize: 20 }} />
                    </Tooltip>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Typography
            sx={{
              fontSize: 11,
              color: form.gstVerified ? '#10b981' : '#94A3B8',
              mt: 0.8,
              fontWeight: form.gstVerified ? 500 : 400,
            }}>
            {form.gstVerified
              ? '✓ State auto-filled from GSTIN'
              : 'Enter your 15-digit GSTIN — state & details auto-fill'}
          </Typography>
        </Box>
      )}

      <SectionHead>Address</SectionHead>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FieldLabel required>Full Address</FieldLabel>
          <TextField
            fullWidth
            size='small'
            multiline
            rows={2}
            placeholder={
              isIndia
                ? 'e.g. 123, Hill View Road, Saputara'
                : 'e.g. 123 Main Street, Suite 4'
            }
            value={form.address}
            onChange={(e) => upd('address', e.target.value)}
            error={!form.address.trim()}
            helperText={!form.address.trim() ? 'Required' : ''}
          />
        </Grid>


        <Grid item xs={12}>
  <FieldLabel required>{isIndia ? 'PIN Code' : 'ZIP Code'}</FieldLabel>
  <TextField
    fullWidth
    size='small'
    placeholder={isIndia ? 'e.g. 422001' : 'e.g. 90210'}
    value={form.pinCode}
    onChange={(e) => {
      const val = e.target.value.replace(/\D/g, '').slice(0, 6); // digits only, max 6
      upd('pinCode', val);
      if (isIndia && val.length === 6) {
        fetchCityStateByPincode(val);
      }
    }}
    inputProps={{ maxLength: 6 }}
    error={!form.pinCode.trim()}
    helperText={!form.pinCode.trim() ? 'Required' : isIndia ? 'City & State auto-fill on 6 digits' : ''}
  />
</Grid>
        
        
        {/* MOBILE: full width on xs, half on sm */}

       <Grid item xs={12}>
          <FieldLabel required>State</FieldLabel>
          <TextField
            select
            fullWidth
            size='small'
            value={form.state}
            onChange={(e) => upd('state', e.target.value)}
            error={!form.state}
            helperText={!form.state ? 'Required' : ''}
            SelectProps={DROPDOWN_SELECT_PROPS}>
            <MenuItem value=''>
              <em>Select State</em>
            </MenuItem>
            {(isIndia ? INDIA_STATES : US_STATES).map((s) => (
              <MenuItem key={s} value={s} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
  <FieldLabel required>City</FieldLabel>
  {isIndia && cities.length > 0 ? (
    <Autocomplete
      fullWidth
      options={cities}
      getOptionLabel={(o) =>
        typeof o === 'string' ? o : o.cityName || ''
      }
      renderOption={(props, o) => (
        <Box
          component='li'
          {...props}
          sx={{ px: '12px !important', py: '8px !important' }}>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: '#0F172A',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.3,
              }}>
              {o.cityName}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.2 }}>
              {o.cityCode}
            </Typography>
          </Box>
        </Box>
      )}
      inputValue={form.city}
      onInputChange={(_, val, reason) => {
        if (reason === 'input') upd('city', val);
      }}
      onChange={(_, v) => {
        if (!v) {
          upd('city', '');
          upd('cityCode', '');
        } else if (typeof v === 'string') {
          upd('city', v);
        } else {
          upd('city', v.cityName || '');
          upd('cityCode', v.cityCode || '');
        }
      }}
      freeSolo
      value={form.city}
      isOptionEqualToValue={(o, v) =>
        o.cityName === v || o.cityCode === v
      }
      componentsProps={{
        paper: {
          sx: {
            width: '100%',
            minWidth: 260,
            mt: 0.5,
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            border: '1px solid #E2E8F0',
            '& .MuiAutocomplete-listbox': {
              maxHeight: 220,
              py: 0.5,
              '& .MuiAutocomplete-option': {
                minHeight: 'unset',
                alignItems: 'flex-start',
              },
              '& .MuiAutocomplete-option.Mui-focused': {
                background: 'rgba(26,86,219,0.06)',
              },
              '& .MuiAutocomplete-option[aria-selected="true"]': {
                background: 'rgba(26,86,219,0.1)',
              },
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          placeholder='Select or type city'
          error={!form.city.trim()}
          helperText={!form.city.trim() ? 'Required' : ''}
          sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: 2,
      },
      '& .MuiAutocomplete-input': {
        minWidth: '110px !important',
      },
    }}
        />
      )}
    />
  ) : (
    <TextField
      fullWidth
      size='small'
      placeholder={isIndia ? 'e.g. Bhilwara' : 'e.g. Los Angeles'}
      value={form.city}
      onChange={(e) => upd('city', e.target.value)}
      error={!form.city.trim()}
      helperText={!form.city.trim() ? 'Required' : ''}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 2,
          minWidth:128,
        },
      }}
    />
  )}
</Grid>

        {/* City Code is never asked for (any country). India auto-fills it from
            the city dropdown; others fall back to DEFAULT_CITY_CODE on save. */}

 

    

        <Grid item xs={12} sm={6}>
          <FieldLabel required>Google Map URL</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='https://maps.google.com/...'
            value={form.mapLocation}
            onChange={(e) => upd('mapLocation', e.target.value)}
            error={!form.mapLocation.trim()}
            helperText={!form.mapLocation.trim() ? 'Required' : ''}
          />
        </Grid>
      </Grid>

      <SectionHead sx={{ mt: 2 }}>Contact</SectionHead>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FieldLabel required>Mobile</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder={isIndia ? '+91 98765 43210' : '+1 555-123-4567'}
            value={form.phone}
            onChange={(e) => upd('phone', e.target.value)}
            error={!form.phone.trim()}
            helperText={!form.phone.trim() ? 'Required' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Reception No</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='e.g. 0253-123456'
            value={form.receptionNo}
            onChange={(e) => upd('receptionNo', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel required>Email</FieldLabel>
          <TextField
            fullWidth
            size='small'
            type='email'
            placeholder='info@hotel.com'
            value={form.email}
            onChange={(e) => upd('email', e.target.value)}
            error={
              !!form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
            }
            helperText={
              !form.email
                ? 'Required'
                : form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
                  ? 'Invalid email'
                  : ''
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Website</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='https://yourhotel.com'
            value={form.website}
            onChange={(e) => upd('website', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FieldLabel>Food License Number</FieldLabel>
          <TextField
            fullWidth
            size='small'
            placeholder='e.g. 10020042000123'
            value={form.foodLicenseNumber}
            onChange={(e) => upd('foodLicenseNumber', e.target.value)}
          />
        </Grid>
        {/* Restaurant DB Name hidden  always sent as empty string to API.
            form.restaurantDbName stays '' in state since no UI sets it. */}
      </Grid>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 2 — Property Setup
══════════════════════════════════════════════ */
function Step2({ form, upd }) {
  const setFloors = (val) => {
    const n = parseInt(val) || 0;
    const rooms = [...(form.rooms || [])];
    if (n > rooms.length) for (let i = rooms.length; i < n; i++) rooms.push('');
    else rooms.splice(n);
    upd('floorCount', val);
    upd('rooms', rooms);
  };

  const addArr = (field) => upd(field, [...form[field], '']);
  const setArr = (field, i, v) => {
    const a = [...form[field]];
    a[i] = v;
    upd(field, a);
  };
  const delArr = (field, i) => {
    const a = [...form[field]];
    a.splice(i, 1);
    upd(field, a);
  };

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Property Setup
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
        Configure floors, room counts, timings, amenities and nearby
        attractions.
      </Typography>

      <SectionHead>Floors & Rooms</SectionHead>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <FieldLabel required>Number of Floors</FieldLabel>
          <TextField
            fullWidth
            size='small'
            type='number'
            placeholder='e.g. 3'
            value={form.floorCount}
            onChange={(e) => setFloors(e.target.value)}
            inputProps={{ min: 1 }}
            error={!form.floorCount || form.floorCount <= 0}
            helperText={!form.floorCount ? 'Required' : ''}
          />
        </Grid>

        {/* MOBILE: floor room counts go full width on xs */}
        {(form.rooms || []).map((count, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <FieldLabel required>Floor {i + 1} — Room Count</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='number'
              placeholder='e.g. 12'
              value={count}
              onChange={(e) => {
                const r = [...form.rooms];
                r[i] = e.target.value;
                upd('rooms', r);
              }}
              inputProps={{ min: 1 }}
              error={!count || Number(count) <= 0}
              helperText={!count || Number(count) <= 0 ? 'Required' : ''}
            />
          </Grid>
        ))}
      </Grid>

      <SectionHead>Check-in / Check-out</SectionHead>
      {form.is24Hour && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.2,
            borderRadius: 2,
            background: 'rgba(26,86,219,0.06)',
            border: '1px solid rgba(26,86,219,0.18)',
          }}>
          <Typography sx={{ fontSize: 13, color: '#1A56DB', fontWeight: 500 }}>
            ✅ 24-hour hotel — check-in / check-out times not applicable
          </Typography>
        </Box>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {['checkInTime', 'checkOutTime'].map((field) => (
          <Grid item xs={12} sm={6} key={field}>
            <FieldLabel>
              {field === 'checkInTime' ? 'Check-in Time' : 'Check-out Time'}
            </FieldLabel>
            <Box
              sx={{
                border: '1px solid',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                borderColor: form.is24Hour ? 'rgba(0,0,0,0.12)' : '#c4c4c4',
                borderRadius: '8px',
                px: 1.75,
                py: 1.1,
                background: form.is24Hour ? 'rgba(0,0,0,0.04)' : '#fff',
                '&:hover': {
                  borderColor: form.is24Hour ? 'rgba(0,0,0,0.12)' : '#0F172A',
                },
                '&:focus-within': {
                  borderColor: '#1A56DB',
                  borderWidth: '2px',
                  boxShadow: '0 0 0 3px rgba(26,86,219,0.1)',
                },
              }}>
              <input
                type='time'
                value={form[field]}
                onChange={(e) => upd(field, e.target.value)}
                disabled={form.is24Hour}
                style={{
                  border: 'none',
                  outline: 'none',
                  width: '100%',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'transparent',
                  padding: 0,
                  color: form.is24Hour ? 'rgba(0,0,0,0.38)' : '#0F172A',
                  cursor: form.is24Hour ? 'not-allowed' : 'pointer',
                }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      <SectionHead>Amenities</SectionHead>
      <Box sx={{ mb: 3 }}>
        {form.amenities.map((item, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size='small'
              placeholder={`e.g. ${['Swimming Pool', 'WiFi', 'Parking', 'Gym', 'Spa'][i % 5]}`}
              value={item}
              onChange={(e) => setArr('amenities', i, e.target.value)}
              error={i === 0 && !item.trim()}
              helperText={
                i === 0 && !item.trim() ? 'At least one required' : ''
              }
            />
            <IconButton
              size='small'
              onClick={() => addArr('amenities')}
              sx={{
                border: '1px solid #E2E8F0',
                borderRadius: 1.5,
                mt: 0.3,
                '&:hover': { borderColor: '#1A56DB', color: '#1A56DB' },
              }}>
              <Add fontSize='small' />
            </IconButton>
            {form.amenities.length > 1 && (
              <IconButton
                size='small'
                onClick={() => delArr('amenities', i)}
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 1.5,
                  mt: 0.3,
                  '&:hover': { borderColor: '#f43f5e', color: '#f43f5e' },
                }}>
                <Close fontSize='small' />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>

      <SectionHead>Attractions / Nearby Places</SectionHead>
      <Box>
        {form.attractions.map((item, i) => (
          <Box
            key={i}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size='small'
              placeholder={`e.g. ${['Saputara Lake', 'Wilson Hills', 'Dang Forest', 'Hatgad Fort', 'Gira Falls'][i % 5]}`}
              value={item}
              onChange={(e) => setArr('attractions', i, e.target.value)}
              error={i === 0 && !item.trim()}
              helperText={
                i === 0 && !item.trim() ? 'At least one required' : ''
              }
            />
            <IconButton
              size='small'
              onClick={() => addArr('attractions')}
              sx={{
                border: '1px solid #E2E8F0',
                borderRadius: 1.5,
                mt: 0.3,
                '&:hover': { borderColor: '#1A56DB', color: '#1A56DB' },
              }}>
              <Add fontSize='small' />
            </IconButton>
            {form.attractions.length > 1 && (
              <IconButton
                size='small'
                onClick={() => delArr('attractions', i)}
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 1.5,
                  mt: 0.3,
                  '&:hover': { borderColor: '#f43f5e', color: '#f43f5e' },
                }}>
                <Close fontSize='small' />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 3 — Room Types
══════════════════════════════════════════════ */
function Step3({ form, upd }) {
  const addRoom = (name) => {
    const n = name.trim();
    if (!n || form.roomTypes.includes(n)) return;
    upd('roomTypes', [...form.roomTypes, n]);
    upd('roomInput', '');
  };
  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        What room types do you offer?
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 4, lineHeight: 1.6 }}>
        Add your room categories. You can configure detailed rooms & capacity
        from the Rooms section later.
      </Typography>

      <FieldLabel>Quick Add</FieldLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {QUICK_ROOMS.filter((r) => !form.roomTypes.includes(r)).map((r) => (
          <Chip
            key={r}
            label={`+ ${r}`}
            size='small'
            variant='outlined'
            onClick={() => addRoom(r)}
            sx={{
              borderColor: '#CBD5E1',
              color: '#475569',
              cursor: 'pointer',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#1A56DB',
                color: '#1A56DB',
                background: 'rgba(26,86,219,0.05)',
              },
            }}
          />
        ))}
      </Box>

      <FieldLabel required>Custom Room Type</FieldLabel>
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          fullWidth
          size='small'
          placeholder='e.g. Honeymoon Suite — press Enter'
          value={form.roomInput}
          onChange={(e) => upd('roomInput', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') addRoom(form.roomInput);
          }}
        />
        <Button
          variant='contained'
          onClick={() => addRoom(form.roomInput)}
          sx={{
            background: '#1A56DB',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            flexShrink: 0,
            minWidth: 0,
          }}>
          <Add />
        </Button>
      </Box>

      {form.roomTypes.length > 0 ? (
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2.5,
            border: '1px solid #E2E8F0',
            background: '#fff',
          }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mb: 1.5,
            }}>
            Added — {form.roomTypes.length} type
            {form.roomTypes.length !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {form.roomTypes.map((r) => (
              <Chip
                key={r}
                label={r}
                onDelete={() =>
                  upd(
                    'roomTypes',
                    form.roomTypes.filter((x) => x !== r),
                  )
                }
                sx={{
                  background: 'rgba(26,86,219,0.08)',
                  color: '#1A56DB',
                  fontWeight: 600,
                  border: '1px solid rgba(26,86,219,0.2)',
                  '& .MuiChip-deleteIcon': {
                    color: '#1A56DB',
                    '&:hover': { color: '#be123c' },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 5,
            borderRadius: 2.5,
            border: '1px dashed #E2E8F0',
            color: '#94A3B8',
            fontSize: 13,
          }}>
          🛏️ No room types added yet. Use Quick Add or type above.
        </Box>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 4 — Add Room
══════════════════════════════════════════════ */
const AC_TYPES = ['Split AC', 'Central AC', 'No AC'];
const CANCEL_POLICIES = ['Flexible', 'Moderate', 'Strict', 'Non-refundable'];

function StepAddRoom({ form, upd }) {
  const cfg = form.roomConfig;
  const setCfg = (field, val) => upd('roomConfig', { ...cfg, [field]: val });
  const imgRef = useRef();
  const floorCount = parseInt(form.floorCount) || 0;
  
//  ADD THIS: auto-select first room type if none selected
  useEffect(() => {
    if (!cfg.selectedRoomType && form.roomTypes.length > 0) {
      setCfg('selectedRoomType', form.roomTypes[0]);
    }
  }, [form.roomTypes]);

  // Mirror handleQtyChange from AddRoom.js — resize room number array when allocation changes

const handleQtyChange = (floor, val) => {
  const totalInFloor = parseInt(form.rooms[floor - 1]) || 0;
  if (val === '' || val === undefined) {
    const newAlloc = { ...cfg.floorAllocation, [floor]: '' };
    const newRoomNums = { ...cfg.roomNumbers, [floor]: [] };
    upd('roomConfig', { ...cfg, floorAllocation: newAlloc, roomNumbers: newRoomNums });
    return;
  }
  const qty = Math.min(parseInt(val) || 0, totalInFloor);  // ✅ removed Math.max(0,...) minimum of 1
  const newAlloc = { ...cfg.floorAllocation, [floor]: qty };
  const prevNums = cfg.roomNumbers[floor] || [];
  const newNums = Array.from({ length: qty }, (_, i) => prevNums[i] || '');
  const newRoomNums = { ...cfg.roomNumbers, [floor]: newNums };
  upd('roomConfig', { ...cfg, floorAllocation: newAlloc, roomNumbers: newRoomNums });
};

  const handleRoomNumChange = (floor, idx, val) => {
    const updated = [...(cfg.roomNumbers[floor] || [])];
    updated[idx] = val;
    setCfg('roomNumbers', { ...cfg.roomNumbers, [floor]: updated });
  };

  // Room numbers that appear more than once across all floors (for inline error).
  const dupRoomNums = (() => {
    const counts = {};
    Object.values(cfg.roomNumbers || {}).forEach((arr) =>
      (arr || []).forEach((rn) => {
        const v = String(rn ?? '').trim();
        if (v) counts[v] = (counts[v] || 0) + 1;
      }),
    );
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  })();

  const addImages = (files) => {
    const imgs = Array.from(files).map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    setCfg('roomImages', [...cfg.roomImages, ...imgs]);
  };

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Configure room details
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
        Set up rooms for <b>{form.hotelName || 'your hotel'}</b>.
      </Typography>

      {/* Room Type */}
      <Box
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#fff',
        }}>
        <FieldLabel required>Room Type</FieldLabel>
        <TextField
          select
          fullWidth
          size='small'
          value={cfg.selectedRoomType}
          onChange={(e) => setCfg('selectedRoomType', e.target.value)}
          SelectProps={DROPDOWN_SELECT_PROPS}>
          {form.roomTypes.length === 0 ? (
            <MenuItem disabled>No room types yet — go back to Step 4</MenuItem>
          ) : (
            form.roomTypes.map((rt) => (
              <MenuItem key={rt} value={rt} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                {rt}
              </MenuItem>
            ))
          )}
        </TextField>
      </Box>

      {/* Basic details */}
      <Box
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#fff',
        }}>
        <SectionHead>Room Details</SectionHead>

        <Grid container spacing={2}>

          {/* <Grid item xs={6} sm={4}>
            <FieldLabel required>Total Rooms</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 1 }}
              value={cfg.totalRooms}
              onChange={(e) => setCfg('totalRooms', e.target.value)}
            />
          </Grid> */}
          <input
  type='hidden'
  value={cfg.totalRooms || '0'}
  onChange={() => {}}
/>

          <Grid item xs={6} sm={4}>
            <FieldLabel required>Max Adults Occupancy Per Room</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 1 }}
              value={cfg.capacity}
              onChange={(e) => setCfg('capacity', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldLabel>Room Size (sq ft)</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='number'
              value={cfg.roomSize}
              onChange={(e) => setCfg('roomSize', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldLabel>Wifi Password</FieldLabel>
            <TextField
              fullWidth
              size='small'
              value={cfg.wifiPass}
              onChange={(e) => setCfg('wifiPass', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldLabel required>AC Type</FieldLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={cfg.acType}
              onChange={(e) => setCfg('acType', e.target.value)}
              SelectProps={DROPDOWN_SELECT_PROPS}>
              {AC_TYPES.map((t) => (
                <MenuItem key={t} value={t} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4}>
            <FieldLabel required>Cancellation Policy</FieldLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={cfg.cancellationPolicy}
              onChange={(e) => setCfg('cancellationPolicy', e.target.value)}
              SelectProps={DROPDOWN_SELECT_PROPS}>
              {CANCEL_POLICIES.map((p) => (
                <MenuItem key={p} value={p} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={cfg.refundable}
              onChange={(e) => setCfg('refundable', e.target.checked)}
              sx={{
                width: 44, height: 24, padding: 0,
                '& .MuiSwitch-switchBase': {
                  padding: 0, margin: '2px', transitionDuration: '300ms',
                  '&.Mui-checked': {
                    transform: 'translateX(20px)', color: '#fff',
                    '& + .MuiSwitch-track': { backgroundColor: '#1A56DB', opacity: 1 },
                  },
                },
                '& .MuiSwitch-thumb': {
                  width: 20, height: 20,
                  boxShadow: '0 2px 4px rgba(15,23,42,0.2)',
                },
                '& .MuiSwitch-track': {
                  borderRadius: 12, backgroundColor: '#CBD5E1', opacity: 1,
                },
              }}
            />
            <Typography
              sx={{ fontSize: 14, fontWeight: 500, color: '#475569', ml: 1 }}>
              Refundable
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Floor allocation — mirrors AddRoom.js logic exactly */}
      {floorCount > 0 &&
        (() => {
          const anyUnallocated = Array.from(
            { length: floorCount },
            (_, i) => i + 1,
          ).some((floor) => !(parseInt(cfg.floorAllocation[floor]) > 0));
          return (
            <Box
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: 2.5,
                border: `1px solid ${anyUnallocated ? '#f43f5e' : '#E2E8F0'}`,
                background: '#fff',
                transition: 'border-color 0.2s',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}>
                <SectionHead sx={{ mb: 0 }}>
                  Floor Wise Room Allocation
                </SectionHead>
                {anyUnallocated && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ErrorOutline sx={{ color: '#f43f5e', fontSize: 15 }} />
                    <Typography
                      sx={{ fontSize: 11, color: '#f43f5e', fontWeight: 600 }}>
                      All floors must be allocated
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5,1fr)',
                    mb: 1,
                    px: 1,
                    minWidth: 340,
                  }}>
                  {[
                    'Floor',
                    'Total Rooms',
                    'Used',
                    'Available',
                    'Allocate *',
                  ].map((h) => (
                    <Typography
                      key={h}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: h === 'Allocate *' ? '#f43f5e' : '#94A3B8',
                        textTransform: 'uppercase',
                      }}>
                      {h}
                    </Typography>
                  ))}
                </Box>
                {Array.from({ length: floorCount }, (_, i) => i + 1).map(
                  (floor) => {
                    const totalInFloor = parseInt(form.rooms[floor - 1]) || 0;
                    const allocated = cfg.floorAllocation[floor] ?? '';
                   //  Only flag as missing if truly never set (undefined/empty string)
const missing = cfg.floorAllocation[floor] === undefined || cfg.floorAllocation[floor] === '';
                    return (
                      <Box
                        key={floor}
                        sx={{
                          borderTop: '1px solid #F1F5F9',
                          py: 1,
                          borderRadius: 1,
                          background: missing
                            ? 'rgba(244,63,94,0.03)'
                            : 'transparent',
                        }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(5,1fr)',
                            alignItems: 'center',
                            px: 1,
                            minWidth: 340,
                          }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            {floor}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#1A56DB' }}>
                            {totalInFloor}
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#f43f5e' }}>
                            0
                          </Typography>
                          <Typography sx={{ fontSize: 13, color: '#10b981' }}>
                            {totalInFloor}
                          </Typography>
                          <Box>
                            <TextField
                              size='small'
                              type='number'
                              value={allocated}
                              onChange={(e) =>
                                handleQtyChange(floor, e.target.value)
                              }
                              inputProps={{ min: 0, max: totalInFloor }}
                              error={missing}
                              sx={{
                                width: 75,
                                '& .MuiOutlinedInput-root': {
                                  borderColor: missing ? '#f43f5e' : undefined,
                                },
                              }}
                            />
                            {missing && (
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: '#f43f5e',
                                  mt: 0.3,
                                }}>
                                Required
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Individual room number inputs per slot — same as AddRoom.js */}
                        {allocated > 0 && (
                          <Box sx={{ mt: 1.5, px: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                mb: 1,
                                color: '#0F172A',
                              }}>
                              Floor {floor} — {cfg.selectedRoomType || 'Room'}{' '}
                              Numbers
                            </Typography>
                            <Grid container spacing={1.5}>
                              {Array.from({ length: allocated }).map(
                                (_, idx) => (
                                  <Grid item xs={6} sm={3} key={idx}>
                                    <TextField
                                      size='small'
                                      fullWidth
                                      label={`Room #${idx + 1}`}
                                      value={
                                        (cfg.roomNumbers[floor] || [])[idx] ||
                                        ''
                                      }
                                      error={dupRoomNums.has(
                                        String(
                                          (cfg.roomNumbers[floor] || [])[idx] ||
                                            '',
                                        ).trim(),
                                      )}
                                      helperText={
                                        dupRoomNums.has(
                                          String(
                                            (cfg.roomNumbers[floor] || [])[
                                              idx
                                            ] || '',
                                          ).trim(),
                                        )
                                          ? 'Duplicate'
                                          : ''
                                      }
                                      onChange={(e) =>
                                        handleRoomNumChange(
                                          floor,
                                          idx,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </Grid>
                                ),
                              )}
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    );
                  },
                )}
              </Box>
            </Box>
          );
        })()}

      {/* Room images */}
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#fff',
        }}>
        <SectionHead>Upload Room Images</SectionHead>
        <input
          ref={imgRef}
          type='file'
          multiple
          accept='image/*'
          style={{ display: 'none' }}
          onChange={(e) => {
            addImages(e.target.files);
            e.target.value = '';
          }}
        />
        <Box
          onClick={() => imgRef.current?.click()}
          sx={{
            border: '2px dashed #E2E8F0',
            borderRadius: 2,
            p: { xs: 2.5, md: 3 },
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#1A56DB',
              background: 'rgba(26,86,219,0.02)',
            },
          }}>
          <CloudUpload sx={{ color: '#94A3B8', fontSize: 32, mb: 0.5 }} />
          <Typography sx={{ color: '#64748B', fontSize: 13 }}>
            Click to upload room photos
          </Typography>
        </Box>
        {cfg.roomImages.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
            {cfg.roomImages.map((img, i) => (
              <Box
                key={i}
                sx={{
                  position: 'relative',
                  width: { xs: 68, md: 80 },
                  height: { xs: 68, md: 80 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #E2E8F0',
                }}>
                <Box
                  component='img'
                  src={img.preview}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size='small'
                  onClick={() =>
                    setCfg(
                      'roomImages',
                      cfg.roomImages.filter((_, idx) => idx !== i),
                    )
                  }
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: 'rgba(0,0,0,0.5)',
                    color: '#fff',
                    p: 0.3,
                  }}>
                  <Close fontSize='small' />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 6 — Package Setup
══════════════════════════════════════════════ */
const WEEK_TYPES = ['All Week', 'Weekday', 'Weekend'];

function StepPackage({ form, upd }) {
  const cfg = form.packageConfig;
  const setCfg = (field, val) => upd('packageConfig', { ...cfg, [field]: val });


  //  ADD THIS: auto-select first room type if none selected
  useEffect(() => {
    if (!cfg.selectedRoomType && form.roomTypes.length > 0) {
      setCfg('selectedRoomType', form.roomTypes[0]);
    }
  }, [form.roomTypes]);

  const addSlab = () =>
    setCfg('priceSlabs', [
      ...cfg.priceSlabs,
      { label: '', basePrice: '' },
    ]);
 
  const delSlab = (i) => {
    if (cfg.priceSlabs.length === 1) return;
    setCfg(
      'priceSlabs',
      cfg.priceSlabs.filter((_, idx) => idx !== i),
    );
  };

  const setSlab = (i, field, val) => {
    const slabs = [...cfg.priceSlabs];
    slabs[i] = { ...slabs[i], [field]: val };
    setCfg('priceSlabs', slabs);
  };

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Create package &amp; pricing
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 3, lineHeight: 1.6 }}>
        Set pricing for <b>{form.hotelName || 'your hotel'}</b>. You can add
        more slabs for different guest configurations.
      </Typography>

      {/* Hotel + Room Type */}
      <Box
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#fff',
        }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FieldLabel>Hotel</FieldLabel>
            <TextField
              fullWidth
              size='small'
              value={form.hotelName || 'Your Hotel'}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FieldLabel required>Room Type</FieldLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={cfg.selectedRoomType}
              onChange={(e) => setCfg('selectedRoomType', e.target.value)}
              error={!cfg.selectedRoomType}
              helperText={!cfg.selectedRoomType ? 'Required' : ''}
              SelectProps={DROPDOWN_SELECT_PROPS}>
              <MenuItem value='' disabled>
                Select room type
              </MenuItem>
              {form.roomTypes.map((rt) => (
                <MenuItem key={rt} value={rt} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                  {rt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Package info */}
      <Box
        sx={{
          p: 2.5,
          mb: 2.5,
          borderRadius: 2.5,
          border: '1.5px solid rgba(26,86,219,0.18)',
          background: 'rgba(26,86,219,0.025)',
        }}>
        <SectionHead>Package Information</SectionHead>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FieldLabel required>Package Name</FieldLabel>
            <TextField
              fullWidth
              size='small'
              placeholder='e.g. Summer Serenity Package'
              value={cfg.packageName}
              onChange={(e) => setCfg('packageName', e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FieldLabel required>From Date</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='date'
              value={cfg.fromDate}
              onChange={(e) => setCfg('fromDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FieldLabel required>To Date</FieldLabel>
            <TextField
              fullWidth
              size='small'
              type='date'
              value={cfg.toDate}
              error={
                !!(
                  cfg.toDate &&
                  cfg.fromDate &&
                  new Date(cfg.toDate) < new Date(cfg.fromDate)
                )
              }
              helperText={
                cfg.toDate &&
                cfg.fromDate &&
                new Date(cfg.toDate) < new Date(cfg.fromDate)
                  ? 'Must be after From date'
                  : ''
              }
              onChange={(e) => setCfg('toDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FieldLabel required>Week Type</FieldLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={cfg.weekType}
              onChange={(e) => setCfg('weekType', e.target.value)}
              SelectProps={DROPDOWN_SELECT_PROPS}>
              <MenuItem value='' disabled>
                Select
              </MenuItem>
              {WEEK_TYPES.map((w) => (
                <MenuItem key={w} value={w} sx={{ whiteSpace: 'normal', fontSize: 13, py: 1 }}>
                  {w}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FieldLabel>Description</FieldLabel>
            <TextField
              fullWidth
              size='small'
              value={cfg.description}
              onChange={(e) => setCfg('description', e.target.value)}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Price slabs — GST auto-calculated matching ManagePackage.js */}
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2.5,
          border: '1px solid #E2E8F0',
          background: '#fff',
        }}>
        <SectionHead>Price Details</SectionHead>

        {cfg.priceSlabs.map((slab, i) => (
          <Box
            key={i}
            sx={{
              mb: 2.5,
              pb: i < cfg.priceSlabs.length - 1 ? 2.5 : 0,
              borderBottom:
                i < cfg.priceSlabs.length - 1 ? '1px solid #F1F5F9' : 'none',
              position: 'relative',
            }}>
            {cfg.priceSlabs.length > 1 && (
              <IconButton
                size='small'
                onClick={() => delSlab(i)}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  color: '#CBD5E1',
                  '&:hover': { color: '#f43f5e' },
                }}>
                <Close fontSize='small' />
              </IconButton>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={7}>
                <FieldLabel>Label (optional)</FieldLabel>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='e.g. Standard, Peak Season, Weekend'
                  value={slab.label || ''}
                  onChange={(e) => setSlab(i, 'label', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <FieldLabel required>Price</FieldLabel>
                <TextField
                  fullWidth
                  size='small'
                  type='number'
                  placeholder='Enter amount'
                  value={slab.basePrice}
                  onChange={(e) => setSlab(i, 'basePrice', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button
          startIcon={<Add />}
          onClick={addSlab}
          size='small'
          variant='outlined'
          sx={{
            borderColor: '#1A56DB',
            color: '#1A56DB',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            mt: 1,
            '&:hover': { background: 'rgba(26,86,219,0.05)' },
          }}>
          + Add Another Price Slab
        </Button>
      </Box>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 5 — Restaurant & Dining
══════════════════════════════════════════════ */
function Step4({ form, upd }) {
  const addArea = (name) => {
    const n = name.trim();
    if (!n || form.restaurantAreas.find((a) => a.name === n)) return;
    upd('restaurantAreas', [...form.restaurantAreas, { name: n, tables: 4 }]);
    upd('areaInput', '');
  };
  const removeArea = (name) =>
    upd(
      'restaurantAreas',
      form.restaurantAreas.filter((a) => a.name !== name),
    );
  const setTables = (name, val) =>
    upd(
      'restaurantAreas',
      form.restaurantAreas.map((a) =>
        a.name === name ? { ...a, tables: Math.max(1, Number(val) || 1) } : a,
      ),
    );
  const totalTables = form.restaurantAreas.reduce((s, a) => s + a.tables, 0);

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Restaurant &amp; Dining Setup
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 4, lineHeight: 1.6 }}>
        Configure your restaurant layout. Skip if you only offer rooms.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.5,
          borderRadius: 2.5,
          border: '1.5px solid',
          borderColor: form.hasRestaurant ? '#1A56DB' : '#E2E8F0',
          background: form.hasRestaurant ? 'rgba(26,86,219,0.04)' : '#fff',
          mb: 3,
          transition: 'all 0.2s',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flex: 1,
            mr: 1,
          }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              background: form.hasRestaurant
                ? 'linear-gradient(135deg,#1A56DB,#3b82f6)'
                : '#F1F5F9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 22,
              transition: 'all 0.25s',
              flexShrink: 0,
            }}>
            🍽️
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: '#0F172A',
                fontSize: { xs: 13, md: 14 },
              }}>
              This property has a restaurant
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              Enable to configure areas and table layout
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={form.hasRestaurant}
          onChange={(e) => upd('hasRestaurant', e.target.checked)}
          sx={{
            flexShrink: 0,
            width: 44,
            height: 24,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: '2px',
              transitionDuration: '300ms',
              '&.Mui-checked': {
                transform: 'translateX(20px)',
                color: '#fff',
                '& + .MuiSwitch-track': {
                  backgroundColor: '#1A56DB',
                  opacity: 1,
                },
              },
            },
            '& .MuiSwitch-thumb': {
              width: 20, height: 20,
              boxShadow: '0 2px 4px rgba(15,23,42,0.2)',
            },
            '& .MuiSwitch-track': {
              borderRadius: 12,
              backgroundColor: '#CBD5E1',
              opacity: 1,
            },
          }}
        />
      </Box>

      {form.hasRestaurant && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}>
          <Box sx={{ mb: 3 }}>
            <FieldLabel>Restaurant Name</FieldLabel>
            <TextField
              fullWidth
              size='small'
              placeholder='e.g. The Garden Bistro'
              value={form.restaurantName}
              onChange={(e) => upd('restaurantName', e.target.value)}
            />
          </Box>

          <FieldLabel>Quick Add Dining Areas</FieldLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {QUICK_AREAS.filter(
              (a) => !form.restaurantAreas.find((x) => x.name === a),
            ).map((a) => (
              <Chip
                key={a}
                label={`+ ${a}`}
                size='small'
                variant='outlined'
                onClick={() => addArea(a)}
                sx={{
                  borderColor: '#CBD5E1',
                  color: '#475569',
                  cursor: 'pointer',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: '#1A56DB',
                    color: '#1A56DB',
                    background: 'rgba(26,86,219,0.05)',
                  },
                }}
              />
            ))}
          </Box>

          <FieldLabel>Custom Area</FieldLabel>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <TextField
              fullWidth
              size='small'
              placeholder='e.g. Terrace Lounge — press Enter'
              value={form.areaInput}
              onChange={(e) => upd('areaInput', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addArea(form.areaInput);
              }}
            />
            <Button
              variant='contained'
              onClick={() => addArea(form.areaInput)}
              sx={{
                background: '#1A56DB',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                flexShrink: 0,
                minWidth: 0,
              }}>
              <Add />
            </Button>
          </Box>

          {form.restaurantAreas.length > 0 ? (
            <>
              <FieldLabel>Areas & Table Count</FieldLabel>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {form.restaurantAreas.map((area) => (
                  <Grid item xs={12} sm={6} key={area.name}>
                    {/* MOBILE: wrap inner content so stepper doesn't overflow */}
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: '1px solid #E2E8F0',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
                      }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          background: 'rgba(26,86,219,0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 20,
                          flexShrink: 0,
                        }}>
                        🪑
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 80 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: '#0F172A',
                            fontSize: 13,
                          }}>
                          {area.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                          {area.tables} table{area.tables !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          ml: 'auto',
                        }}>
                        <IconButton
                          size='small'
                          onClick={() => setTables(area.name, area.tables - 1)}
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid #E2E8F0',
                            borderRadius: 1.5,
                            '&:hover': { background: 'rgba(26,86,219,0.08)' },
                          }}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                        <TextField
                          size='small'
                          type='number'
                          value={area.tables}
                          onChange={(e) => setTables(area.name, e.target.value)}
                          inputProps={{
                            min: 1,
                            style: {
                              textAlign: 'center',
                              width: 36,
                              padding: '4px 0',
                              fontWeight: 700,
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                            '& fieldset': { borderColor: '#E2E8F0' },
                          }}
                        />
                        <IconButton
                          size='small'
                          onClick={() => setTables(area.name, area.tables + 1)}
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid #E2E8F0',
                            borderRadius: 1.5,
                            '&:hover': { background: 'rgba(26,86,219,0.08)' },
                          }}>
                          <Add sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() => removeArea(area.name)}
                          sx={{
                            color: '#CBD5E1',
                            '&:hover': { color: '#f43f5e' },
                          }}>
                          <Close fontSize='small' />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background:
                    'linear-gradient(135deg,rgba(26,86,219,0.07),rgba(26,86,219,0.03))',
                  border: '1px solid rgba(26,86,219,0.18)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                <Typography
                  sx={{ fontWeight: 700, color: '#1A56DB', fontSize: 13 }}>
                  Total Capacity
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{ fontWeight: 800, color: '#0F172A', fontSize: 20 }}>
                      {form.restaurantAreas.length}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                      Areas
                    </Typography>
                  </Box>
                  <Box sx={{ width: 1, background: '#E2E8F0' }} />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      sx={{ fontWeight: 800, color: '#0F172A', fontSize: 20 }}>
                      {totalTables}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#64748B' }}>
                      Tables
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 4,
                borderRadius: 2.5,
                border: '1px dashed #E2E8F0',
                color: '#94A3B8',
                fontSize: 13,
              }}>
              🪑 No dining areas added yet.
            </Box>
          )}
        </motion.div>
      )}

      {!form.hasRestaurant && (
        <Box sx={{ textAlign: 'center', py: 6, color: '#94A3B8' }}>
          <Typography sx={{ fontSize: 32, mb: 1 }}>🍽️</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            Toggle on above if your property includes a restaurant.
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>
            You can always set this up later from the Restaurant section.
          </Typography>
        </Box>
      )}
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 5 — Packages & Pricing
══════════════════════════════════════════════ */
function Step5({ form, upd }) {
  const addPkg = () =>
    upd('packages', [...form.packages, { name: '', price: '' }]);
  const delPkg = (i) =>
    upd(
      'packages',
      form.packages.filter((_, idx) => idx !== i),
    );
  const setPkg = (i, field, val) => {
    const p = [...form.packages];
    p[i] = { ...p[i], [field]: val };
    upd('packages', p);
  };

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Set up packages &amp; pricing
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 4, lineHeight: 1.6 }}>
        Add at least one package. Advanced pricing options are available from
        Manage Packages later.
      </Typography>

      {form.packages.map((pkg, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.05 }}>
          <Box
            sx={{
              p: 2.5,
              mb: 2,
              borderRadius: 2.5,
              position: 'relative',
              border: '1.5px solid',
              borderColor: i === 0 ? '#1A56DB' : '#E2E8F0',
              background: i === 0 ? 'rgba(26,86,219,0.03)' : '#fff',
            }}>
            {i === 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -11,
                  left: 14,
                  background: '#1A56DB',
                  color: '#fff',
                  px: 1.5,
                  py: 0.2,
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                }}>
                PRIMARY
              </Box>
            )}
            {form.packages.length > 1 && (
              <IconButton
                size='small'
                onClick={() => delPkg(i)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#CBD5E1',
                  '&:hover': { color: '#f43f5e' },
                }}>
                <Close fontSize='small' />
              </IconButton>
            )}
            {/* MOBILE: name and price each take full width on xs */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <FieldLabel required>Package Name</FieldLabel>
                <TextField
                  fullWidth
                  size='small'
                  placeholder='e.g. Deluxe Room Plan'
                  value={pkg.name}
                  onChange={(e) => setPkg(i, 'name', e.target.value)}
                  error={!pkg.name.trim()}
                  helperText={!pkg.name.trim() ? 'Required' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <FieldLabel required>Price per Night (₹)</FieldLabel>
                <TextField
                  fullWidth
                  size='small'
                  type='number'
                  placeholder='e.g. 3500'
                  value={pkg.price}
                  onChange={(e) => setPkg(i, 'price', e.target.value)}
                  error={!pkg.price}
                  helperText={!pkg.price ? 'Required' : ''}
                />
              </Grid>
            </Grid>
          </Box>
        </motion.div>
      ))}

      <Button
        startIcon={<Add />}
        onClick={addPkg}
        variant='outlined'
        sx={{
          borderColor: '#1A56DB',
          color: '#1A56DB',
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { background: 'rgba(26,86,219,0.05)' },
        }}>
        Add Another Package
      </Button>
    </>
  );
}

/* ══════════════════════════════════════════════
   STEP 6 — Review & Go Live
══════════════════════════════════════════════ */
function Step6({ form, isIndia }) {
  const stars = '★'.repeat(form.starRating) + '☆'.repeat(5 - form.starRating);
  const pc = form.packageConfig;
  const hasPackage = pc.packageName && pc.priceSlabs.some((s) => s.basePrice);
  const currency =
    CURRENCIES.find((c) => c.code === form.currency) || CURRENCIES[0];

  const Card = ({ label, children }) => (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2.5,
        border: '1px solid #E2E8F0',
        background: '#fff',
        height: '100%',
      }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: '#94A3B8',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          mb: 1.5,
        }}>
        {label}
      </Typography>
      {children}
    </Box>
  );

  return (
    <>
      <Typography
        variant='h4'
        fontWeight={800}
        sx={{
          color: '#0F172A',
          mb: 0.5,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.25rem', md: '1.75rem' },
        }}>
        Almost there! Review your setup
      </Typography>
      <Typography
        sx={{ color: '#64748B', fontSize: 14, mb: 4, lineHeight: 1.6 }}>
        Everything looks great. Click <b>Go Live</b> to launch your hotel.
      </Typography>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card label='🏨 Hotel Profile'>
            {form.logoPreview && (
              <Box
                component='img'
                src={form.logoPreview}
                sx={{
                  width: 48,
                  height: 48,
                  objectFit: 'contain',
                  borderRadius: 1.5,
                  border: '1px solid #E2E8F0',
                  mb: 1,
                }}
              />
            )}
            <Typography
              sx={{ fontWeight: 700, color: '#0F172A', fontSize: 15 }}>
              {form.hotelName || '—'}
            </Typography>
            {form.description && (
              <Typography sx={{ color: '#64748B', fontSize: 12, mt: 0.5 }}>
                {form.description}
              </Typography>
            )}
            <Typography
              sx={{ color: '#f59e0b', fontSize: 18, mt: 1, letterSpacing: 2 }}>
              {stars}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
              {currency.name} ({currency.symbol}) ·{' '}
              {form.is24Hour
                ? '24-Hour Hotel'
                : `In: ${form.checkInTime || '—'} / Out: ${form.checkOutTime || '—'}`}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.5 }}>
              📷 {form.images.length} image{form.images.length !== 1 ? 's' : ''}{' '}
              uploaded
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card label='📍 Location & Contact'>
            {isIndia && (
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
                <Typography sx={{ fontSize: 14 }}>🇮🇳</Typography>
                <Typography
                  sx={{ fontWeight: 700, color: '#1A56DB', fontSize: 12 }}>
                  India
                </Typography>
                {form.gstNumber && (
                  <Box
                    sx={{
                      ml: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.4,
                    }}>
                    {form.gstVerified && (
                      <CheckCircle sx={{ color: '#10b981', fontSize: 14 }} />
                    )}
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: '#64748B',
                        fontFamily: 'monospace',
                      }}>
                      {form.gstNumber}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Typography
              sx={{ color: '#0F172A', fontSize: 13, fontWeight: 500 }}>
              {form.address || '—'}
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 13 }}>
              {[form.city, form.state, form.pinCode]
                .filter(Boolean)
                .join(', ') || '—'}
            </Typography>
            {form.phone && (
              <Typography sx={{ color: '#64748B', fontSize: 13, mt: 0.5 }}>
                📞 {form.phone}
                {form.receptionNo ? ` / ${form.receptionNo}` : ''}
              </Typography>
            )}
            {form.email && (
              <Typography sx={{ color: '#64748B', fontSize: 13 }}>
                ✉️ {form.email}
              </Typography>
            )}
            {form.mapLocation && (
              <Typography sx={{ color: '#1A56DB', fontSize: 12, mt: 0.5 }}>
                🗺️ Map linked
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card label='🏗️ Property Setup'>
            <Typography sx={{ fontSize: 13, color: '#0F172A' }}>
              <b>{form.floorCount || 0}</b> floor
              {form.floorCount !== '1' ? 's' : ''} &nbsp;·&nbsp;
              <b>
                {(form.rooms || []).reduce((s, r) => s + (Number(r) || 0), 0)}
              </b>{' '}
              total rooms
            </Typography>
            {form.amenities.filter((a) => a.trim()).length > 0 && (
              <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.8 }}>
                ✅ {form.amenities.filter((a) => a.trim()).length} amenit
                {form.amenities.filter((a) => a.trim()).length !== 1
                  ? 'ies'
                  : 'y'}{' '}
                &nbsp;·&nbsp;
                {form.attractions.filter((a) => a.trim()).length} attraction
                {form.attractions.filter((a) => a.trim()).length !== 1
                  ? 's'
                  : ''}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card label={`🛏️ Room Types (${form.roomTypes.length})`}>
            {form.roomTypes.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                {form.roomTypes.map((r) => (
                  <Chip
                    key={r}
                    label={r}
                    size='small'
                    sx={{
                      background: 'rgba(26,86,219,0.08)',
                      color: '#1A56DB',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>
                No room types added
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card label={`💰 Packages (${hasPackage ? 1 : 0})`}>
            {hasPackage ? (
              <Box>
                <Typography
                  sx={{ color: '#0F172A', fontSize: 13, fontWeight: 600 }}>
                  {pc.packageName}
                </Typography>
                {pc.weekType && (
                  <Typography sx={{ color: '#64748B', fontSize: 12, mt: 0.3 }}>
                    Week: {pc.weekType}
                  </Typography>
                )}
                {(pc.fromDate || pc.toDate) && (
                  <Typography sx={{ color: '#64748B', fontSize: 12 }}>
                    {pc.fromDate || '—'} → {pc.toDate || '—'}
                  </Typography>
                )}
                {pc.priceSlabs
                  .filter((s) => s.basePrice)
                  .map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#475569' }}>
                        {s.label || `Price ${i + 1}`}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: '#10b981',
                          fontWeight: 700,
                        }}>
                        {currency.symbol}
                        {Number(s.basePrice).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>
                No packages added
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card
            label={`🍽️ Restaurant${form.hasRestaurant ? ` · ${form.restaurantAreas.length} areas · ${form.restaurantAreas.reduce((s, a) => s + a.tables, 0)} tables` : ''}`}>
            {form.hasRestaurant ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {form.restaurantAreas.map((a) => (
                  <Box
                    key={a.name}
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      borderRadius: 2,
                      background: 'rgba(26,86,219,0.07)',
                      border: '1px solid rgba(26,86,219,0.15)',
                    }}>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: '#1A56DB' }}>
                      {a.name} · {a.tables}t
                    </Typography>
                  </Box>
                ))}
                {form.restaurantAreas.length === 0 && (
                  <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>
                    No areas configured
                  </Typography>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: '#94A3B8', fontSize: 13 }}>
                Hotel-only property
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          p: 2.5,
          borderRadius: 2.5,
          background:
            'linear-gradient(135deg,rgba(16,185,129,0.07),rgba(16,185,129,0.03))',
          border: '1px solid rgba(16,185,129,0.22)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '50%',
            background: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 22,
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
          }}>
          ✓
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#065f46', fontSize: 14 }}>
            Your hotel is ready to go live!
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#10b981', mt: 0.2 }}>
            Click "Go Live" below to start accepting bookings.
          </Typography>
        </Box>
      </Box>
    </>
  );
}

/* ══════════════════════════════════════════════
   MAIN WIZARD
══════════════════════════════════════════════ */
// Public entry point. Restaurant-only companies are routed to the Restaurant
// Setup Wizard here, BEFORE the hotel wizard's hooks run. This keeps the
// React rules-of-hooks happy (hook ordering inside HotelSetupWizardHotel
// stays stable) and is invisible to any outer caller / import.
export default function HotelSetupWizard(props) {
  if (isRestaurantCompany()) {
    return <RestaurantSetupWizard {...props} />;
  }
  return <HotelSetupWizardHotel {...props} />;
}

function HotelSetupWizardHotel({
  apiBaseUrl = defaultConfig.API_BASE_URL,
  getToken = () => sessionStorage.getItem('token'),
  getCompanyId = () => sessionStorage.getItem('companyId'),
  onComplete = null,
  onSkip = null,
  brandName = 'Hotel Desk',
  brandSub = 'Hotel Management',
  country = 'international',
}) {
  const navigate = useNavigate();
  const fileRef = useRef();
  const imgRef = useRef();

  const [phase, setPhase] = useState('welcome');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [packageSkipped, setPackageSkipped] = useState(false);
  const [cities, setCities] = useState([]);

  const [form, setForm] = useState({
    hotelName: '',
    description: '',
    starRating: 3,
    currency: country === 'india' ? 'INR' : 'USD',
    is24Hour: false,
    logoFile: null,
    logoPreview: null,
    images: [],
    address: '',
    city: '',
    cityCode: '',
    state: '',
    pinCode: '',
    mapLocation: '',
    phone: '',
    receptionNo: '',
    email: '',
    website: '',
    gstNumber: '',
    foodLicenseNumber: '',
    restaurantDbName: '',
    gstFetching: false,
    gstVerified: false,
    gstError: '',
    floorCount: '',
    rooms: [],
    checkInTime: '',
    checkOutTime: '',
    amenities: [''],
    attractions: [''],
    roomTypes: [],
    roomInput: '',
    hasRestaurant: false,
    restaurantName: '',
    restaurantAreas: [],
    areaInput: '',
    packages: [{ name: '', price: '' }],

    // Step 4 — Add Room
    roomConfig: {
      selectedRoomType: '',
      totalRooms: '',
      capacity: '',
      roomSize: '',
      wifiPass: '',
      refundable: true,
      acType: 'Split AC',
      cancellationPolicy: 'Flexible',
      floorAllocation: {},
      roomNumbers: {},
      roomImages: [],
    },

    // Step 6 — Package Setup
    packageConfig: {
      selectedRoomType: '',
      packageName: '',
      fromDate: '',
      toDate: '',
      weekType: '',
      description: '',
      priceSlabs: [
        { label: '', basePrice: '' },
      ],
    },
  });

  const isIndia = country === 'india' || form.currency === 'INR';
  // Clear state/city/cityCode ONLY on an explicit India<->US country switch.
  // Base this on `country` alone — NOT on form.currency. Previously the company
  // prefill auto-set currency=INR, which flipped `isIndia` and WIPED the freshly
  // prefilled state/city/cityCode, so the hotel wizard looked empty even though
  // the company data was loaded. (Restaurant wizard has no such effect.)
  const prevIsIndiaRef = useRef(country === 'india');
  useEffect(() => {
    const ci = country === 'india';
    if (prevIsIndiaRef.current !== ci) {
      prevIsIndiaRef.current = ci;
      setForm((f) => ({ ...f, state: '', city: '', cityCode: '' }));
    }
  }, [country]);
  const upd = (field, val) => setForm((f) => ({ ...f, [field]: val }));
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    const pick = (d, ...keys) => {
      for (const k of keys) {
        const v = d[k];
        if (v !== undefined && v !== null && String(v).trim() !== '')
          return String(v).trim();
      }
      return '';
    };

    // Map currency symbols/names → codes
    const normalizeCurrency = (val) => {
      if (!val) return '';
      const map = {
        '₹': 'INR',
        $: 'USD',
        '€': 'EUR',
        '£': 'GBP',
        'د.إ': 'AED',
        '﷼': 'SAR',
        S$: 'SGD',
        A$: 'AUD',
      };
      if (map[val]) return map[val];
      const upper = val.toUpperCase();
      const known = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'SGD', 'AUD'];
      return known.includes(upper) ? upper : '';
    };

    const isIndiaCountry = (c) =>
      ['india', 'in'].includes((c || '').toLowerCase());
    const isUSCountry = (c) =>
      ['us', 'usa', 'united states', 'united states of america'].includes(
        (c || '').toLowerCase(),
      );

    const applyCompany = (d, markPrefilled = false) => {
      if (!d) return;

      // Redirect to the correct wizard based on stored country
      const savedCountry = d.country || d.Country || '';
      if (isIndiaCountry(savedCountry) && country !== 'india') {
        navigate('/setup/india');
        return;
      }
      if (isUSCountry(savedCountry) && country === 'india') {
        navigate('/setup');
        return;
      }

      // Auto-derive currency from country, normalizing symbols to codes
      const rawCurrency = pick(d, 'currency', 'Currency');
      const savedCurrency = normalizeCurrency(rawCurrency);
      const derivedCurrency = isIndiaCountry(savedCountry)
        ? 'INR'
        : isUSCountry(savedCountry)
          ? 'USD'
          : savedCurrency || (country === 'india' ? 'INR' : 'USD');

      setForm((f) => ({
        ...f,
        hotelName:
          pick(
            d,
            'name',
            'companyName',
            'CompanyName',
            'Name',
            'HotelName',
            'hotelName',
          ) || f.hotelName,
        description:
          pick(
            d,
            'description',
            'Description',
            'about',
            'About',
            'tagline',
            'Tagline',
          ) || f.description,
        currency: derivedCurrency || f.currency,
        email:
          pick(d, 'email', 'Email', 'emailAddress', 'EmailAddress') || f.email,
        phone:
          pick(
            d,
            'mobile',
            'Mobile',
            'phone',
            'Phone',
            'contactNo',
            'ContactNo',
            'mobileNo',
            'MobileNo',
          ) || f.phone,
        receptionNo:
          pick(
            d,
            'receptionNo',
            'ReceptionNo',
            'phone2',
            'Phone2',
            'landline',
            'Landline',
            'altPhone',
          ) || f.receptionNo,
        address:
          pick(
            d,
            'address',
            'Address',
            'Address1',
            'address1',
            'streetAddress',
            'StreetAddress',
            'fullAddress',
          ) || f.address,
        state:
          pick(
            d,
            'state',
            'State',
            'stateName',
            'StateName',
            'location',
            'Location',
          ) || f.state,
        city:
          pick(
            d,
            'city',
            'City',
            'cityName',
            'CityName',
            'district',
            'District',
          ) || f.city,
        cityCode: pick(d, 'cityCode', 'CityCode') || f.cityCode,
        pinCode:
          pick(
            d,
            'pincode',
            'Pincode',
            'pinCode',
            'PinCode',
            'zipCode',
            'ZipCode',
            'zip',
            'Zip',
          ) || f.pinCode,
        mapLocation:
          pick(
            d,
            'mapLocation',
            'MapLocation',
            'mapUrl',
            'MapUrl',
            'googleMap',
            'GoogleMap',
            'mapLink',
          ) || f.mapLocation,
        gstNumber:
          pick(
            d,
            'gstNumber',
            'GstNumber',
            'gst',
            'GST',
            'GSTIN',
            'gstin',
            'gstNo',
            'GstNo',
          ) || f.gstNumber,
        foodLicenseNumber:
          pick(
            d,
            'foodLicense',
            'FoodLicense',
            'foodLicenseNumber',
            'FoodLicenseNumber',
            'foodLicenseNo',
            'FoodLicenseNo',
          ) || f.foodLicenseNumber,
        website:
          pick(
            d,
            'website',
            'Website',
            'websiteUrl',
            'WebsiteUrl',
            'siteUrl',
            'SiteUrl',
            'url',
            'Url',
          ) || f.website,
        restaurantDbName:
          pick(
            d,
            'restaurants',
            'Restaurants',
            'restaurantName',
            'RestaurantName',
            'restaurantDb',
          ) || f.restaurantDbName,
      }));
      if (markPrefilled) setPrefilled(true);
    };

    try {
      const stored = sessionStorage.getItem('companyDetails');
      if (stored) {
        applyCompany(JSON.parse(stored), true);
      }
    } catch {
      /* ignore */
    }

    const fetchCompany = async () => {
      const companyId = getCompanyId();
      const token = getToken();
      if (!companyId || !token) return;
      try {
        const res = await fetch(
          `${apiBaseUrl}/account/GetCompanyDetails/${encodeURIComponent(companyId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.ok) {
          const d = await res.json();
          applyCompany(d, true);
          sessionStorage.setItem('companyDetails', JSON.stringify(d));
        }
      } catch (e) {
        console.warn('[Wizard] GetCompanyDetails failed:', e);
      }
    };
    fetchCompany();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/Home/GetAllCity`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (res.ok) setCities(await res.json());
      } catch (e) {
        console.warn('City load failed:', e);
      }
    };
    load();
  }, []);

  const fetchGSTDetails = async (gstin) => {
    const g = gstin.toUpperCase().replace(/\s/g, '');
    if (!GSTIN_REGEX.test(g)) return;
    const stateCode = g.slice(0, 2);
    const detectedState = GSTIN_STATE[stateCode];
    setForm((f) => ({
      ...f,
      state: detectedState || f.state,
      gstFetching: true,
      gstVerified: false,
      gstError: '',
    }));
    try {
      const res = await fetch(
        `${apiBaseUrl}/Home/GetGSTDetails?gstNumber=${g}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );
      if (res.ok) {
        const d = await res.json();
        setForm((f) => ({
          ...f,
          hotelName: f.hotelName || d.tradeName || d.legalName || f.hotelName,
          address: f.address || d.address || f.address,
          city: f.city || d.city || f.city,
          state: detectedState || d.state || f.state,
          pinCode: f.pinCode || d.pinCode || d.pin || f.pinCode,
          gstFetching: false,
          gstVerified: true,
          gstError: '',
        }));
      } else {
        setForm((f) => ({
          ...f,
          gstFetching: false,
          gstVerified: !!detectedState,
          gstError: detectedState ? '' : 'GST lookup unavailable',
        }));
      }
    } catch {
      setForm((f) => ({
        ...f,
        gstFetching: false,
        gstVerified: !!detectedState,
        gstError: '',
      }));
    }
  };

// Default CityCode used as a last-resort fallback. When the pincode/district
// cannot be matched to any CityMaster record, we assign this so the hotel
// record is always saved with a valid (non-NULL) CityCode. The accompanying
// console.warn tells us which city name needs to be added to the master data.
const DEFAULT_CITY_CODE = '101';

// Normalize a place name for tolerant comparison: lowercase, drop diacritics,
// strip special characters (dots, hyphens, parentheses, etc.), and collapse
// whitespace. This makes "Bengaluru (Bangalore)", "BENGALURU", and
// " bengaluru " all compare equal.
const normalizeCityName = (s) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFKD')               // separate accents so diacritics can drop out
    .replace(/[\u0300-\u036f]/g, '') // remove the separated accent marks
    .replace(/[^a-z0-9\s]/g, ' ')    // special chars → space
    .replace(/\s+/g, ' ')            // collapse runs of whitespace
    .trim();

// Resolve a CityCode for a district/city name against the loaded CityMaster
// (cities). Tries exact → partial → first-word matches, all on normalized
// names, then falls back to DEFAULT_CITY_CODE. Always returns a non-empty
// string, so callers can rely on CityCode never being NULL/empty.
const resolveCityCode = (cityName) => {
  const target = normalizeCityName(cityName);

  // Support both camelCase (GetAllCity response) and PascalCase field names.
  const nameOf = (c) => c?.cityName ?? c?.CityName ?? '';
  const codeOf = (c) => c?.cityCode ?? c?.CityCode ?? '';

  let hit = null;
  if (target && Array.isArray(cities) && cities.length) {
    // 1. Exact match after normalization (case/space/special-char tolerant).
    hit = cities.find((c) => normalizeCityName(nameOf(c)) === target);

    // 2. Partial match — one name contains the other. Handles suffixes and
    //    alternate naming: "Bengaluru" vs "Bengaluru Urban", "Delhi" vs
    //    "New Delhi", "Mumbai" vs "Mumbai Suburban".
    if (!hit) {
      hit = cities.find((c) => {
        const n = normalizeCityName(nameOf(c));
        return n && (n.includes(target) || target.includes(n));
      });
    }

    // 3. First-word match — "Mumbai Suburban" → "Mumbai".
    if (!hit) {
      const firstWord = target.split(' ')[0];
      hit = cities.find(
        (c) => normalizeCityName(nameOf(c)).split(' ')[0] === firstWord,
      );
    }
  }

  const code = hit ? String(codeOf(hit) ?? '').trim() : '';
  if (code) return code;

  // No CityMaster record matched (or cities not loaded yet). Log loudly so the
  // missing city can be added to the master, then fall back to DEFAULT_CITY_CODE
  // so CityCode is NEVER persisted as NULL/empty.
  console.warn(
    `[Wizard] CityCode lookup FAILED for "${cityName}" — no match among ` +
      `${Array.isArray(cities) ? cities.length : 0} CityMaster records. ` +
      `Falling back to CityCode=${DEFAULT_CITY_CODE}. ` +
      `Add "${cityName}" to the City master to fix this mapping.`,
  );
  return DEFAULT_CITY_CODE;
};

const fetchCityStateByPincode = async (pincode) => {
  if (!isIndia || pincode.length !== 6) return;
  try {
    const res = await fetch(
      `${apiBaseUrl}/Home/GetCityStatebyPincode?pincode=${encodeURIComponent(pincode)}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: '',
      },
    );
    if (res.ok) {
      const json = await res.json();
      if (!json?.status || !json?.data) return;

      const d     = json.data;
      const city  = d?.district ?? '';  // "Bhilwara"
      const state = d?.state    ?? '';  // "Rajasthan"

      // ── Resolve the CityCode from the loaded CityMaster (cities) ──
      // The pincode API returns a *district* name, which does not always
      // match a CityMaster record exactly (case, spaces, special chars,
      // suffixes like "Urban"/"Suburban", or alternate spellings). We try
      // progressively looser strategies, then fall back to a default code
      // so CityCode is NEVER stored as NULL/empty. See resolveCityCode below.
      const cityCode = resolveCityCode(city);

      setForm((f) => ({
        ...f,
        city,
        cityCode,
        state: state || f.state,
      }));

      if (city || state) {
        toast.success(
          cityCode
            ? `Auto-filled: ${city} (${cityCode}), ${state} ✓`
            : `Auto-filled: ${city}, ${state} ✓`,
        );
      }
    }
  } catch (e) {
    console.warn('[Wizard] GetCityStatebyPincode failed:', e);
  }
};

  // When the user reaches Step 2 (Location & Contact, step index 1), if a PIN
  // code was pre-filled from the saved company record, auto-fetch City & State
  // once — exactly as if the user had just typed it. Waits for the city master
  // (cities) to load so CityCode can resolve, and the ref guard ensures it runs
  // only once (manual typing is already handled by the field's onChange).
  const pinAutoFetched = useRef(false);
  useEffect(() => {
    if (pinAutoFetched.current || !isIndia || step !== 1) return;
    const pin = (form.pinCode || '').trim();
    if (pin.length === 6 && cities.length > 0) {
      pinAutoFetched.current = true;
      fetchCityStateByPincode(pin);
    }
  }, [step, isIndia, form.pinCode, cities]);

  const validateStep = () => {
    switch (step) {
      case 0:
        if (!form.hotelName.trim()) {
          toast.error('Hotel name is required');
          return false;
        }
        if (!form.description.trim()) {
          toast.error('Description is required');
          return false;
        }
        if (!form.logoFile) {
          toast.error('Hotel logo is required');
          return false;
        }
        if (form.images.length === 0) {
          toast.error('Upload at least one hotel image');
          return false;
        }
        return true;
      case 1:
        if (!form.address.trim()) {
          toast.error('Address is required');
          return false;
        }
        if (!form.city.trim()) {
          toast.error('City is required');
          return false;
        }
        // City Code is not collected in the wizard - no validation needed.
        if (!form.state) {
          toast.error('State is required');
          return false;
        }
        if (!form.pinCode.trim()) {
          toast.error('Pincode is required');
          return false;
        }
        if (!form.mapLocation.trim()) {
          toast.error('Google Map URL is required');
          return false;
        }
        if (!form.phone.trim()) {
          toast.error('Mobile number is required');
          return false;
        }
        if (!form.email.trim()) {
          toast.error('Email is required');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
          toast.error('Invalid email format');
          return false;
        }
        return true;
      case 2: {
        if (!form.floorCount || Number(form.floorCount) <= 0) {
          toast.error('Number of floors is required');
          return false;
        }
        if ((form.rooms || []).some((r) => !r || Number(r) <= 0)) {
          toast.error('Please enter room count for all floors');
          return false;
        }
        if (form.amenities.filter((a) => a.trim()).length === 0) {
          toast.error('At least one amenity is required');
          return false;
        }
        if (form.attractions.filter((a) => a.trim()).length === 0) {
          toast.error('At least one attraction is required');
          return false;
        }
        return true;
      }
      case 3:
        if (form.roomTypes.length === 0) {
          toast.error('Please add at least one room type');
          return false;
        }
        return true;
      case 4: {
        if (!form.roomConfig.selectedRoomType) {
          toast.error('Please select a room type');
          return false;
        }
        // if (
        //   !form.roomConfig.totalRooms ||
        //   Number(form.roomConfig.totalRooms) <= 0
        // ) {
        //   toast.error('Total rooms is required');
        //   return false;
        // }
        if (
          !form.roomConfig.capacity ||
          Number(form.roomConfig.capacity) <= 0
        ) {
          toast.error('Capacity is required');
          return false;
        }
        const floorCount = parseInt(form.floorCount) || 0;
        if (floorCount > 0) {
        const unallocated = Array.from({ length: floorCount }, (_, i) => i + 1).filter(
  (floor) => String(form.roomConfig.floorAllocation[floor]) === '' || 
             form.roomConfig.floorAllocation[floor] === undefined
);
if (unallocated.length > 0) {
  toast.error(
    `Please set allocation for floor${unallocated.length > 1 ? 's' : ''}: ${unallocated.join(', ')} (0 is allowed)`
  );
  return false;
}
        }

        // Room numbers must be unique across ALL floors.
        {
          const allRoomNums = [];
          Object.values(form.roomConfig.roomNumbers || {}).forEach((arr) =>
            (arr || []).forEach((rn) => {
              const v = String(rn ?? '').trim();
              if (v) allRoomNums.push(v);
            }),
          );
          const seenRN = new Set();
          const dupRN = new Set();
          allRoomNums.forEach((rn) => {
            if (seenRN.has(rn)) dupRN.add(rn);
            else seenRN.add(rn);
          });
          if (dupRN.size > 0) {
            toast.error(
              `Duplicate room number(s): ${[...dupRN].join(', ')}. Room numbers must be unique.`,
            );
            return false;
          }
        }
        return true;
      }
      case 6: {
        if (!form.packageConfig.packageName.trim()) {
          toast.error('Package name is required');
          return false;
        }
        if (!form.packageConfig.selectedRoomType) {
          toast.error('Please select a room type for the package');
          return false;
        }
        if (!form.packageConfig.fromDate) {
          toast.error('From date is required');
          return false;
        }
        if (!form.packageConfig.toDate) {
          toast.error('To date is required');
          return false;
        }
        if (!form.packageConfig.priceSlabs[0]?.basePrice) {
          toast.error('Please enter a base price for at least one slab');
          return false;
        }
        return true;
      }
      default:
        return true;
    }
  };

  const goNext = () => {
    if (!validateStep()) return;
    if (step < STEPS.length - 1) {
      setDirection(1);
      setStep((s) => s + 1);
    } else {
      handleFinish();
    }
  };

  const goBack = () => {
    if (step === 0) {
      setPhase('welcome');
    } else {
      setDirection(-1);
      setStep((s) => s - 1);
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    setSaveError('');
    let savedHotelId = null;
    const companyId = getCompanyId();
    const token = getToken();

    if (!token) {
      const msg = 'Authentication token missing. Please log in again.';
      setSaveError(msg);
      toast.error(msg);
      setSaving(false);
      return;
    }

    const authHdr = { Authorization: `Bearer ${token}` };

    try {
      const fd = new FormData();
      fd.append('Name', form.hotelName || 'My Hotel');
      fd.append('Location', form.state || '');
      fd.append('Address', form.address || '');
      fd.append('City', form.city || '');
      // Safety net: CityCode must never be saved as NULL/empty. If it wasn't
      // resolved during pincode auto-fill (or the user cleared it), fall back
      // to the default code. resolveCityCode already applies this for the
      // pincode flow; this guards manual edits and the City Autocomplete path.
      fd.append('CityCode', (form.cityCode && String(form.cityCode).trim()) || DEFAULT_CITY_CODE);
      fd.append('Pincode', form.pinCode || '');
      fd.append('Mobile', form.phone || '');
      fd.append('Email', form.email || '');
      fd.append('Rating', String(form.starRating || 3));
      fd.append('Currency', form.currency || (isIndia ? 'INR' : 'USD'));
      fd.append('Country', isIndia ? 'India' : 'US');
      fd.append('CompanyId', companyId || '');
      fd.append('Description', form.description || '');
      fd.append('MapLocation', form.mapLocation || '');
      fd.append(
        'Restaurants',
        form.hasRestaurant
          ? form.restaurantDbName || form.restaurantName || 'Restaurant'
          : '',
      );
      fd.append('Riceptionno', form.receptionNo || '');
      fd.append('Is24Hour', form.is24Hour ? 'true' : 'false');
      fd.append('FloorCount', String(parseInt(form.floorCount) || 0));
      if (!form.is24Hour) {
        if (form.checkInTime) fd.append('CheckInTime', form.checkInTime);
        if (form.checkOutTime) fd.append('CheckOutTime', form.checkOutTime);
      }
      if (form.gstNumber?.trim()) fd.append('GstNumber', form.gstNumber.trim());
      if (form.foodLicenseNumber?.trim())
        fd.append('FoodLicenseNumber', form.foodLicenseNumber.trim());
      if (form.website) fd.append('Website', form.website);
      if (form.logoFile) fd.append('HotelLogo', form.logoFile);
      form.rooms.forEach((r) => {
        if (r) fd.append('Rooms', parseInt(r));
      });
      form.amenities.forEach((a) => {
        if (a.trim()) fd.append('Amenities', a.trim());
      });
      form.attractions.forEach((a) => {
        if (a.trim()) fd.append('Attractions', a.trim());
      });
      form.images.forEach((img) => {
        if (img.file) fd.append('Images', img.file);
      });

      const hotelRes = await fetch(`${apiBaseUrl}/Home/SaveHotel`, {
        method: 'POST',
        headers: authHdr,
        body: fd,
      });

      // ---- Company tax profile ------------------------------------------
      // GST is India-only. A non-Indian property must never charge Indian GST,
      // so default it to No-Tax. The backend flips CompanyMaster.GstApplicable
      // from TaxMode, which turns GST off across the whole app (room booking,
      // addons, cover charge, package and the bill).
      // Best-effort: a failure here must never block the hotel from saving.
      try {
        const taxProfile = isIndia
          ? { TaxMode: 'GST', TaxRate: 18 }
          : { TaxMode: 'None', TaxRate: 0 };
        const taxRes = await fetch(`${apiBaseUrl}/account/CompanyTaxProfile`, {
          method: 'POST',
          headers: { ...authHdr, 'Content-Type': 'application/json' },
          body: JSON.stringify({ CompanyId: Number(companyId), ...taxProfile }),
        });
        if (taxRes.ok) {
          // Keep this session in step so the UI hides GST immediately.
          const cd = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
          cd.taxMode = taxProfile.TaxMode;
          cd.taxRate = taxProfile.TaxRate;
          cd.gstApplicable = isIndia;
          sessionStorage.setItem('companyDetails', JSON.stringify(cd));
        }
      } catch (e) {
        console.warn('[Wizard] tax profile save skipped:', e);
      }

      if (hotelRes.ok) {
        const hotelData = await hotelRes.json().catch(() => ({}));
        savedHotelId =
          hotelData?.hotelId ?? hotelData?.id ?? hotelData?.Id ?? null;

        if (!savedHotelId) {
          const listRes = await fetch(
            `${apiBaseUrl}/Home/GetHotels/${encodeURIComponent(companyId)}`,
            { headers: authHdr },
          );
          if (listRes.ok) {
            const hotels = await listRes.json().catch(() => []);
            if (Array.isArray(hotels) && hotels.length > 0) {
              const latest = hotels[hotels.length - 1];
              savedHotelId =
                latest?.id ?? latest?.Id ?? latest?.hotelId ?? null;
            }
          }
        }
      } else {
        const errText = await hotelRes.text().catch(() => '');
        console.error('[Wizard] SaveHotel failed:', hotelRes.status, errText);
        const msg = `Hotel save failed (${hotelRes.status}). Please try again.`;
        setSaveError(msg);
        toast.error(msg);
        setSaving(false);
        return;
      }

      if (form.roomTypes.length > 0) {
        await Promise.allSettled(
          form.roomTypes.map((rt) =>
            fetch(
              `${apiBaseUrl}/Home/SaveRoomType/${encodeURIComponent(rt)}/${encodeURIComponent(companyId)}`,
              { method: 'POST', headers: authHdr },
            ),
          ),
        );
      }

      /* ── Save Room (Step 4) — matches AddRoom.js FormData exactly ── */
      const rc = form.roomConfig;
      // The "Total Rooms" input is hidden, so rc.totalRooms is never set by the
      // UI. Derive the total from the per-floor allocation (which the Floor
      // Wise Room Allocation table DOES populate) — e.g. { "1": 5, "2": 3 } → 8.
      // Without this the save guard below was always false and SaveRoom never ran.
      const totalAllocated = Object.values(rc.floorAllocation || {}).reduce(
        (sum, v) => sum + (parseInt(v, 10) || 0),
        0,
      );
      console.log('[Wizard] room-save check:', {
        savedHotelId,
        selectedRoomType: rc?.selectedRoomType,
        totalRooms: rc?.totalRooms,
        totalAllocated,
        roomNumbers: rc?.roomNumbers,
      });
      if (!(rc.selectedRoomType && totalAllocated > 0 && savedHotelId)) {
        console.warn('[Wizard] Room save SKIPPED — guard failed. Need all of: '
          + 'selectedRoomType, allocated rooms (> 0), savedHotelId. See values above.');
      }
      if (rc.selectedRoomType && totalAllocated > 0 && savedHotelId) {
        const roomFd = new FormData();
        // Defaults first (same order as AddRoom.js)
        roomFd.append('Price', '0');
        roomFd.append('Tax', '0');
        roomFd.append('OtherCharges', '0');
        roomFd.append('Inclusions', 'only room');
        roomFd.append('Description', 'only room');
        // Form fields (same key names as AddRoom.js form state)
        roomFd.append('hotelId', String(savedHotelId));
        roomFd.append('type', rc.selectedRoomType);
        // Use the allocation-derived total (rc.totalRooms is unused/empty).
        roomFd.append('totalroom', String(totalAllocated));
        roomFd.append('capacity', rc.capacity || '2');
        roomFd.append('roomSize', rc.roomSize || '0');
        roomFd.append('wifipass', rc.wifiPass || '');
        roomFd.append('acType', rc.acType || AC_TYPES[0]);
        roomFd.append('status', 'Available');
        roomFd.append('refundable', rc.refundable ? 'true' : 'false');
        roomFd.append(
          'cancellationPolicy',
          rc.cancellationPolicy || CANCEL_POLICIES[0],
        );
        // Floor allocation JSON  { "1": 5, "2": 3 }
        roomFd.append('FloorRooms', JSON.stringify(rc.floorAllocation));
        // Room numbers JSON  { "1": ["101","102"], "2": ["201"] }
        const cleanNums = {};
        Object.entries(rc.roomNumbers).forEach(([floor, arr]) => {
          // Crash-proof: a slot may be null/undefined/number, not just a
          // string. Coerce to string before trimming so a named room (or any
          // odd value) can never throw `n.trim is not a function` and silently
          // abort the whole save before the SaveRoom request is even sent.
          const filled = (arr || [])
            .map((n) => (n === null || n === undefined ? '' : String(n).trim()))
            .filter((n) => n.length > 0);
          if (filled.length > 0) cleanNums[parseInt(floor)] = filled;
        });
        console.log('[Wizard] SaveRoom RoomNumbers payload:', cleanNums);
        roomFd.append('RoomNumbers', JSON.stringify(cleanNums));
        rc.roomImages.forEach((img) => {
          if (img.file) roomFd.append('Images', img.file);
        });

        const roomRes = await fetch(`${apiBaseUrl}/Home/SaveRoom`, {
          method: 'POST',
          headers: authHdr,
          body: roomFd,
        }).catch((e) => {
          console.warn('[Wizard] SaveRoom network error:', e);
          return null;
        });
        if (roomRes && !roomRes.ok) {
          const err = await roomRes.text().catch(() => '');
          console.error('[Wizard] SaveRoom failed:', roomRes.status, err);
          toast.error(`Room save failed (${roomRes.status}). Check console.`);
        }
      }

      /* ── Save Package (Step 6) — matches ManagePackage.js payload ── */
      const pc = form.packageConfig;
      if (pc.packageName && pc.priceSlabs[0]?.basePrice && savedHotelId) {
        setPackageSkipped(false);
        const today = new Date();
        const nextYr = new Date(today);
        nextYr.setFullYear(nextYr.getFullYear() + 1);
        const toISO = (d) =>
          d instanceof Date ? d.toISOString().split('T')[0] : d;

        // Fetch real roomTypeId from API (same as ManagePackage.js uses data[0].id)
        let roomTypeId = 0;
        if (pc.selectedRoomType && companyId) {
          try {
            const rtRes = await fetch(
              `${apiBaseUrl}/Home/GetAllRoomType/${encodeURIComponent(companyId)}`,
              { headers: authHdr },
            );
            if (rtRes.ok) {
              const rtData = await rtRes.json();
              const matched = rtData.find((r) => {
                const name =
                  r.roomtype ?? r.Roomtype ?? r.RoomType ?? r.roomType ?? '';
                return name.toLowerCase() === pc.selectedRoomType.toLowerCase();
              });
              if (matched) {
                const idKey = Object.keys(matched).find(
                  (k) => k.toLowerCase() === 'id',
                );
                roomTypeId = idKey ? Number(matched[idKey]) : 0;
              }
            }
          } catch {
            /* fallback to 0 */
          }
        }

        if (roomTypeId === 0) {
          setPackageSkipped(true);
          toast.warning(
            'Package skipped: could not resolve room type ID. Add the package manually after setup.',
          );
        } else {
          // Build the payload separately so we can log EXACTLY what is saved.
          // Compare these values (hotelId, roomTypeId, fromDate/toDate) against
          // the booking package lookup (GetPackageReportforbookings) — the
          // booking filters by hotelId + roomTypeId + check-in/out date window,
          // so a package only appears when all three line up.
          const pkgPayload = {
            hotelId: savedHotelId,
            roomTypeId,
            packageName: pc.packageName,
            description: pc.description || '',
            weekType: pc.weekType || 'All Week',
            fromDate: pc.fromDate || toISO(today),
            toDate: pc.toDate || toISO(nextYr),
            // Price-only pricing (no adult/child split). Backend kept
            // happy with neutral defaults so existing PackagePriceDetails
            // schema doesn't need to change.
            priceDetails: pc.priceSlabs
              .filter((s) => s.basePrice)
              .map((s) => ({
                adultCount: 0,
                childCount: 0,
                childAge: 0, // backend expects int  send 0 not null
                label: s.label || '',
                price: Number(s.basePrice),
              })),
          };
          console.log('[Wizard] CreatePackage payload:', pkgPayload);
          const pkgRes = await fetch(`${apiBaseUrl}/Home/Create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHdr },
            body: JSON.stringify(pkgPayload),
          });
          if (!pkgRes.ok) {
            const err = await pkgRes.text().catch(() => '');
            console.error('[Wizard] CreatePackage failed:', pkgRes.status, err);
            toast.error(
              `Package save failed (${pkgRes.status}). You can add it manually from Manage Package.`,
            );
            setPackageSkipped(true);
          } else {
            console.log('[Wizard] CreatePackage OK for roomTypeId', roomTypeId);
          }
        } // end else (roomTypeId !== 0)
      } else {
        setPackageSkipped(true);
      }

      if (form.hasRestaurant && form.restaurantAreas.length > 0) {
        await Promise.allSettled(
          form.restaurantAreas.map((area) => {
            const afd = new FormData();
            afd.append('AreaName', area.name);
            afd.append('TableName', area.name);
            afd.append('TableCount', String(area.tables));
            afd.append('Companyid', companyId || '');
            return fetch(`${apiBaseUrl}/home/AddResturentarea`, {
              method: 'POST',
              headers: authHdr,
              body: afd,
            });
          }),
        );
      }
    } catch (e) {
      console.error('[Wizard] Unexpected save error:', e);
      const msg = 'Unexpected error. Check console for details.';
      setSaveError(msg);
      toast.error(msg);
      setSaving(false);
      return;
    }

    setSaving(false);
    localStorage.setItem('hotelSetupDone', 'true');
    toast.success(`${form.hotelName || 'Hotel'} is now live on Hotel Desk! 🎉`, {
      autoClose: 4000,
    });
    if (onComplete) onComplete({ form, savedHotelId });
    else setPhase('done');
  };

  const handleSkip = () => {
    localStorage.setItem('hotelSetupDone', 'true');
    if (onSkip) onSkip();
    else navigate('/admin/dashboard');
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  /* ══ WELCOME PHASE ═════════════════════════════ */
  if (phase === 'welcome') {
    return (
      /* MOBILE: flex column on xs, row on md */
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          fontFamily: 'Inter, sans-serif',
          flexDirection: { xs: 'column', md: 'row' },
        }}>
        {/* MOBILE: left panel hidden on mobile */}
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <LeftPanel step={-1} brandName={brandName} brandSub={brandSub} />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F8FAFF',
            p: { xs: 2, md: 4 },
            overflowY: 'auto',
          }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}>
            <Box sx={{ maxWidth: 520, textAlign: 'center' }}>
              <Box
                sx={{
                  width: { xs: 68, md: 88 },
                  height: { xs: 68, md: 88 },
                  borderRadius: 4,
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg,#0F172A,#1A56DB)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 32, md: 42 },
                  boxShadow: '0 16px 40px rgba(26,86,219,0.3)',
                }}>
                🏨
              </Box>
              <Typography
                variant='h3'
                fontWeight={800}
                sx={{
                  color: '#0F172A',
                  letterSpacing: '-0.03em',
                  mb: 1,
                  fontSize: { xs: '1.6rem', md: '2.5rem' },
                }}>
                Welcome to {brandName}
              </Typography>
              <Typography
                sx={{
                  color: '#64748B',
                  fontSize: { xs: 13, md: 15 },
                  lineHeight: 1.7,
                  mb: 3,
                }}>
                Let's set up your hotel property in just <b>8 quick steps</b>.
                <br />
                It takes about 10 minutes and you can always edit details later.
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: 'center',
                  mb: 5,
                }}>
                {[
                  '🛏️ Room Management',
                  '📅 Booking Wizard',
                  '💳 Payment Tracking',
                  '📊 Live Dashboard',
                  '🎟 Packages & Coupons',
                ].map((f) => (
                  <Box
                    key={f}
                    sx={{
                      px: 2,
                      py: 0.9,
                      borderRadius: 20,
                      background: '#fff',
                      border: '1.5px solid #E2E8F0',
                      fontSize: { xs: 11, md: 13 },
                      fontWeight: 500,
                      color: '#475569',
                      boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
                    }}>
                    {f}
                  </Box>
                ))}
              </Box>
              <Button
                variant='contained'
                size='large'
                endIcon={<ArrowForward />}
                onClick={() => {
                  setPhase('steps');
                  setStep(0);
                  setDirection(1);
                }}
                sx={{
                  background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
                  boxShadow: '0 8px 28px rgba(26,86,219,0.35)',
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: 14, md: 16 },
                  px: { xs: 3, md: 5 },
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    background: 'linear-gradient(135deg,#1741b0,#2563eb)',
                  },
                }}>
                Start Hotel Setup {isIndia ? '🇮🇳' : '🇺🇸'}
              </Button>
              <Typography sx={{ mt: 2, fontSize: 12, color: '#94A3B8' }}>
                ⏱ Takes about 10 minutes &nbsp;·&nbsp;
                <Box
                  component='span'
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    '&:hover': { color: '#64748B' },
                  }}
                  onClick={handleSkip}>
                  Skip for now
                </Box>
              </Typography>
            </Box>
          </motion.div>
        </Box>
      </Box>
    );
  }

  /* ══ DONE PHASE ════════════════════════════════ */
  if (phase === 'done') {
    return (
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          overflow: 'hidden',
          fontFamily: 'Inter, sans-serif',
          flexDirection: { xs: 'column', md: 'row' },
        }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          <LeftPanel
            step={STEPS.length}
            brandName={brandName}
            brandSub={brandSub}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#F8FAFF',
            p: { xs: 2, md: 4 },
            overflowY: 'auto',
          }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}>
            <Box sx={{ maxWidth: 560, textAlign: 'center' }}>
              <Box
                sx={{
                  width: { xs: 76, md: 100 },
                  height: { xs: 76, md: 100 },
                  borderRadius: '50%',
                  mx: 'auto',
                  mb: 3,
                  background: 'linear-gradient(135deg,#10b981,#059669)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 36, md: 48 },
                  boxShadow: '0 12px 40px rgba(16,185,129,0.35)',
                }}>
                ✓
              </Box>
              <Typography
                variant='h3'
                fontWeight={800}
                sx={{
                  color: '#0F172A',
                  letterSpacing: '-0.03em',
                  mb: 1,
                  fontSize: { xs: '1.6rem', md: '2.5rem' },
                }}>
                Hotel Setup Complete!
              </Typography>
              <Typography
                sx={{
                  color: '#64748B',
                  fontSize: { xs: 13, md: 15 },
                  lineHeight: 1.7,
                  mb: 4,
                }}>
                <b>{form.hotelName || 'Your hotel'}</b> is now live on{' '}
                {brandName}.<br />
                Your team can start accepting bookings right away.
              </Typography>
              {/* MOBILE: 2-col grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.5,
                  mb: packageSkipped ? 3 : 5,
                }}>
                {[
                  { icon: '🏨', label: 'Hotel', val: form.hotelName || '—' },
                  {
                    icon: '🛏️',
                    label: 'Room Types',
                    val: `${form.roomTypes.length} Added`,
                  },
                  {
                    icon: '🏗️',
                    label: 'Floors',
                    val: `${form.floorCount || 0} Floor${form.floorCount !== '1' ? 's' : ''}`,
                  },
                  {
                    icon: '🍽️',
                    label: 'Restaurant',
                    val: form.hasRestaurant
                      ? `${form.restaurantAreas.reduce((s, a) => s + a.tables, 0)} Tables`
                      : 'Hotel Only',
                  },
                  {
                    icon: '💰',
                    label: 'Packages',
                    val: packageSkipped
                      ? 'Not Added'
                      : form.packageConfig.packageName
                        ? '1 Ready'
                        : 'None',
                    warn: packageSkipped,
                  },
                  { icon: '🟢', label: 'Status', val: 'Live', green: true },
                ].map((c) => (
                  <Box
                    key={c.label}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1.5px solid ${c.warn ? '#fde68a' : '#E2E8F0'}`,
                      background: c.warn ? '#fffbeb' : '#fff',
                      textAlign: 'left',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.05)',
                    }}>
                    <Typography sx={{ fontSize: 20, mb: 0.8 }}>
                      {c.icon}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#94A3B8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}>
                      {c.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: c.green
                          ? '#10b981'
                          : c.warn
                            ? '#d97706'
                            : '#0F172A',
                        mt: 0.3,
                      }}>
                      {c.val}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {packageSkipped && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2.5,
                    border: '1.5px solid #fde68a',
                    background: '#fffbeb',
                    textAlign: 'left',
                  }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: '#92400e',
                      mb: 0.5,
                    }}>
                    Package not added yet
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: '#78350f',
                      lineHeight: 1.6,
                      mb: 2,
                    }}>
                    For a complete setup, add a package so guests can see rates
                    and book online.
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/admin/managepackage')}
                    sx={{
                      borderColor: '#d97706',
                      color: '#92400e',
                      fontWeight: 700,
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 2.5,
                      '&:hover': {
                        background: '#fef3c7',
                        borderColor: '#b45309',
                      },
                    }}>
                    Add Package Now
                  </Button>
                </Box>
              )}

              <Button
                variant='contained'
                size='large'
                endIcon={<ArrowForward />}
                onClick={() => navigate('/admin/dashboard')}
                sx={{
                  background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
                  boxShadow: '0 8px 28px rgba(26,86,219,0.35)',
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: 14, md: 16 },
                  px: { xs: 3, md: 5 },
                  py: 1.5,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    background: 'linear-gradient(135deg,#1741b0,#2563eb)',
                  },
                }}>
                Go to Dashboard
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Box>
    );
  }

  /* ══ STEPS PHASE ═══════════════════════════════ */
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
        flexDirection: { xs: 'column', md: 'row' },
      }}>
      {/* MOBILE: left panel hidden */}
      <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
        <LeftPanel step={step} brandName={brandName} brandSub={brandSub} />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: '#F8FAFF',
          overflow: 'hidden',
        }}>
        {/* Topbar — MOBILE: compact padding */}
        <Box
          sx={{
            background: '#fff',
            borderBottom: '1px solid #E2E8F0',
            px: { xs: 2, md: 5 },
            py: 1.8,
          }}>
          {prefilled && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.2,
                px: 2,
                py: 0.9,
                borderRadius: 2,
                background: 'rgba(16,185,129,0.07)',
                border: '1px solid rgba(16,185,129,0.2)',
              }}>
              <CheckCircle
                sx={{ color: '#10b981', fontSize: 16, flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: 12, color: '#065f46', fontWeight: 500 }}>
                Fields auto-filled from your account — review and complete any
                missing details.
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, md: 4 },
            }}>
            <Box sx={{ flex: 1, maxWidth: 460 }}>
              <Typography
                sx={{
                  fontSize: 11,
                  color: '#94A3B8',
                  fontWeight: 600,
                  mb: 0.8,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}>
                {/* MOBILE: shorter label */}
                <Box
                  component='span'
                  sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Setup Progress —{' '}
                </Box>
                Step {step + 1} of {STEPS.length}
              </Typography>
              <LinearProgress
                variant='determinate'
                value={progress}
                sx={{
                  height: 7,
                  borderRadius: 4,
                  backgroundColor: '#E2E8F0',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg,#1A56DB,#3b82f6)',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
            <Button
              size='small'
              onClick={handleSkip}
              sx={{
                color: '#94A3B8',
                fontSize: 12,
                textTransform: 'none',
                border: '1px solid #E2E8F0',
                borderRadius: 2,
                px: { xs: 1.5, md: 2 },
                whiteSpace: 'nowrap',
                '&:hover': { color: '#64748B' },
              }}>
              Skip
            </Button>
          </Box>
        </Box>

        {/* Animated form — MOBILE: reduced padding */}
        <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
          <AnimatePresence custom={direction} mode='wait'>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{ duration: 0.26, ease: [0.4, 0, 0.2, 1] }}
              style={{ maxWidth: 760 }}>
              {/* MOBILE: responsive padding via a wrapper Box */}
              <Box sx={{ px: { xs: 2, sm: 4, md: 7 }, py: { xs: 2.5, md: 5 } }}>
                {/* Step badge */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.8,
                    background: 'rgba(26,86,219,0.08)',
                    color: '#1A56DB',
                    border: '1px solid rgba(26,86,219,0.15)',
                    borderRadius: 20,
                    px: 1.8,
                    py: 0.5,
                    fontSize: 11,
                    fontWeight: 700,
                    mb: 2.5,
                  }}>
                  {STEPS[step].icon} Step {step + 1} — {STEPS[step].label}
                </Box>

                {step === 0 && (
                  <Step0
                    form={form}
                    upd={upd}
                    fileRef={fileRef}
                    imgRef={imgRef}
                  />
                )}
                {step === 1 && (
                  <Step1
                    form={form}
                    upd={upd}
                    cities={cities}
                    isIndia={isIndia}
                    fetchGSTDetails={fetchGSTDetails}
                    fetchCityStateByPincode={fetchCityStateByPincode}
                  />
                )}
                {step === 2 && <Step2 form={form} upd={upd} />}
                {step === 3 && <Step3 form={form} upd={upd} />}
                {step === 4 && <StepAddRoom form={form} upd={upd} />}
                {step === 5 && <Step4 form={form} upd={upd} />}
                {step === 6 && <StepPackage form={form} upd={upd} />}
                {step === 7 && <Step6 form={form} isIndia={isIndia} />}
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* Action bar — MOBILE: stacked layout */}
        <Box
          sx={{
            background: '#fff',
            borderTop: '1px solid #E2E8F0',
            px: { xs: 2, md: 5 },
            py: 2.2,
          }}>
          {saveError && (
            <Box
              sx={{
                mb: 1.5,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: 'rgba(244,63,94,0.07)',
                border: '1px solid rgba(244,63,94,0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
              <ErrorOutline
                sx={{ color: '#f43f5e', fontSize: 18, flexShrink: 0 }}
              />
              <Typography
                sx={{ color: '#be123c', fontSize: 13, fontWeight: 500 }}>
                {saveError}
              </Typography>
            </Box>
          )}
          {/* MOBILE: row stays but Continue goes full width below on xs */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}>
            <Button
              onClick={goBack}
              startIcon={<ArrowBack />}
              sx={{
                color: '#475569',
                border: '1.5px solid #E2E8F0',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { background: '#F8FAFF' },
              }}>
              Back
            </Button>
            {/* Step counter — hidden on xs to save space */}
            <Typography
              sx={{
                ml: 'auto',
                mr: 2,
                fontSize: 12,
                color: '#94A3B8',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}>
              {step + 1} / {STEPS.length}
            </Typography>
            <Button
              onClick={goNext}
              disabled={saving}
              variant='contained'
              endIcon={
                saving ? (
                  <CircularProgress size={16} color='inherit' />
                ) : step < STEPS.length - 1 ? (
                  <ArrowForward />
                ) : undefined
              }
              sx={{
                background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
                boxShadow: '0 4px 14px rgba(26,86,219,0.3)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                px: 3.5,
                py: 1.2,
                fontSize: 14,
                /* MOBILE: full width on xs */
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'unset', sm: 140 },
                order: { xs: -1, sm: 0 }, // Continue appears at top on mobile
                '&:hover': {
                  background: 'linear-gradient(135deg,#1741b0,#2563eb)',
                  boxShadow: '0 6px 18px rgba(26,86,219,0.4)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
                  color: '#fff',
                  opacity: 0.75,
                },
              }}>
              {saving
                ? 'Saving…'
                : step === STEPS.length - 1
                  ? '🚀  Go Live!'
                  : 'Continue'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/* ── Left Panel ──────────────────────────────── */
function LeftPanel({
  step,
  brandName = 'Hotel Desk',
  brandSub = 'Hotel Management',
}) {
  const allDone = step >= STEPS.length;
  return (
    <Box
      sx={{
        width: 296,
        minWidth: 296,
        background: 'linear-gradient(160deg,#0F172A 0%,#1e3a6e 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        p: '36px 28px',
        position: 'relative',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(26,86,219,0.14)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -60,
          left: -40,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(59,130,246,0.1)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 5,
          position: 'relative',
          zIndex: 1,
        }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 18,
            boxShadow: '0 4px 12px rgba(26,86,219,0.4)',
          }}>
          {brandName?.[0] || 'H'}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
            {brandName}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            {brandSub}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, mb: 4 }}>
        <Typography
          sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.3, mb: 0.5 }}>
          {allDone ? 'Setup Complete! 🎉' : 'Hotel Setup'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          {allDone
            ? 'Your hotel is live'
            : step < 0
              ? `${STEPS.length} quick steps to go live`
              : `Step ${step + 1} of ${STEPS.length} — ${STEPS[step]?.label}`}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
        {STEPS.map((s, i) => {
          const isDone = allDone || i < step;
          const isActive = !allDone && i === step;
          return (
            <Box key={i}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.8,
                  py: 1.1,
                  px: 1.5,
                  borderRadius: 2,
                  background: isActive ? 'rgba(26,86,219,0.2)' : 'transparent',
                  transition: 'background 0.25s',
                }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    mt: 0.15,
                    background: isDone
                      ? '#10b981'
                      : isActive
                        ? '#1A56DB'
                        : 'transparent',
                    border: `2px solid ${isDone ? '#10b981' : isActive ? '#1A56DB' : 'rgba(255,255,255,0.15)'}`,
                    color:
                      isDone || isActive ? '#fff' : 'rgba(255,255,255,0.35)',
                    boxShadow: isActive
                      ? '0 0 0 4px rgba(26,86,219,0.22)'
                      : 'none',
                    transition: 'all 0.3s',
                  }}>
                  {isDone ? '✓' : i + 1}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13,
                      lineHeight: 1.3,
                      fontWeight: isActive ? 700 : isDone ? 500 : 400,
                      color: isActive
                        ? '#fff'
                        : isDone
                          ? 'rgba(255,255,255,0.6)'
                          : 'rgba(255,255,255,0.35)',
                      transition: 'color 0.25s',
                    }}>
                    {s.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      color: isActive
                        ? 'rgba(255,255,255,0.5)'
                        : 'rgba(255,255,255,0.2)',
                    }}>
                    {s.desc}
                  </Typography>
                </Box>
              </Box>
              {i < STEPS.length - 1 && (
                <Box
                  sx={{
                    width: 2,
                    height: 12,
                    background: isDone ? '#10b981' : 'rgba(255,255,255,0.07)',
                    ml: '22px',
                    my: 0.3,
                    borderRadius: 1,
                    transition: 'background 0.3s',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          pt: 2.5,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          fontSize: 11,
          color: 'rgba(255,255,255,0.25)',
        }}>
        Powered by Go-ERP.Live · Secure Setup
      </Box>
    </Box>
  );
}
